import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { getInactivityDays, parseInactivityMessageTemplatesResult } from '../lib/inactivity';
import { MessageIntrospectionModal, IntrospectableMessage } from '../components/MessageIntrospectionModal';
import { ManualEvaluationModal } from '../components/ManualEvaluationModal';

// Default module color for gradient styling (can be customized based on module context)
const DEFAULT_MODULE_COLOR = '#E8D5C5';

/**
 * Text model pricing (cents per 1M tokens) - mirrors pricing.ts from API
 * Used to calculate costs client-side for display purposes
 */
const TEXT_MODEL_PRICING: Record<string, { inputCentsPerMillion: number; outputCentsPerMillion: number }> = {
  'openai/gpt-4o': { inputCentsPerMillion: 250, outputCentsPerMillion: 1000 },
  'openai/gpt-4o-mini': { inputCentsPerMillion: 15, outputCentsPerMillion: 60 },
  'openai/gpt-4-turbo': { inputCentsPerMillion: 1000, outputCentsPerMillion: 3000 },
  'openai/gpt-4': { inputCentsPerMillion: 3000, outputCentsPerMillion: 6000 },
  'openai/gpt-3.5-turbo': { inputCentsPerMillion: 50, outputCentsPerMillion: 150 },
  'openai/o1': { inputCentsPerMillion: 1500, outputCentsPerMillion: 6000 },
  'openai/o1-mini': { inputCentsPerMillion: 300, outputCentsPerMillion: 1200 },
  'anthropic/claude-3.5-sonnet': { inputCentsPerMillion: 300, outputCentsPerMillion: 1500 },
  'anthropic/claude-3-opus': { inputCentsPerMillion: 1500, outputCentsPerMillion: 7500 },
  'anthropic/claude-3-sonnet': { inputCentsPerMillion: 300, outputCentsPerMillion: 1500 },
  'anthropic/claude-3-haiku': { inputCentsPerMillion: 25, outputCentsPerMillion: 125 },
  'google/gemini-pro': { inputCentsPerMillion: 50, outputCentsPerMillion: 150 },
  'google/gemini-pro-1.5': { inputCentsPerMillion: 125, outputCentsPerMillion: 500 },
  'google/gemini-flash-1.5': { inputCentsPerMillion: 7.5, outputCentsPerMillion: 30 },
  'google/gemini-3-flash-preview': { inputCentsPerMillion: 50, outputCentsPerMillion: 300 },
  'meta-llama/llama-3.1-405b-instruct': { inputCentsPerMillion: 270, outputCentsPerMillion: 270 },
  'meta-llama/llama-3.1-70b-instruct': { inputCentsPerMillion: 52, outputCentsPerMillion: 75 },
  'meta-llama/llama-3.1-8b-instruct': { inputCentsPerMillion: 5.5, outputCentsPerMillion: 5.5 },
  'mistralai/mistral-large': { inputCentsPerMillion: 200, outputCentsPerMillion: 600 },
  'deepseek/deepseek-chat': { inputCentsPerMillion: 14, outputCentsPerMillion: 28 },
};

// Unknown models show $0.00 to make missing pricing immediately visible
const UNKNOWN_PRICING = { inputCentsPerMillion: 0, outputCentsPerMillion: 0 };

/**
 * Normalizes model identifiers for lookup.
 * Handles variations in naming conventions between providers.
 */
function normalizeModelId(model: string): string {
  // Remove any version suffixes that might differ
  // Handles both formats:
  // - Dashed dates: 'openai/gpt-4o-2024-01-01' -> 'openai/gpt-4o'
  // - Compact dates: 'google/gemini-3-flash-preview-20251217' -> 'google/gemini-3-flash-preview'
  return model
    .replace(/-\d{4}-\d{2}-\d{2}$/, '')  // Remove -YYYY-MM-DD suffix
    .replace(/-\d{8}$/, '');              // Remove -YYYYMMDD suffix
}

/**
 * Gets text model pricing from lookup table with normalization fallback.
 * Returns zero pricing for unknown models to make missing pricing visible.
 */
function getTextModelPricing(model: string): { inputCentsPerMillion: number; outputCentsPerMillion: number } {
  // Try exact match first
  if (TEXT_MODEL_PRICING[model]) {
    return TEXT_MODEL_PRICING[model];
  }

  // Try normalized model ID
  const normalizedModel = normalizeModelId(model);
  if (normalizedModel !== model && TEXT_MODEL_PRICING[normalizedModel]) {
    return TEXT_MODEL_PRICING[normalizedModel];
  }

  // Return zero pricing for unknown models - makes missing pricing visible
  console.warn(`[Pricing] Unknown model "${model}" - showing $0.00`);
  return UNKNOWN_PRICING;
}

/**
 * TTS model pricing (cents per 1M characters) - mirrors pricing.ts from API
 */
const TTS_MODEL_PRICING: Record<string, { centsPerMillionChars: number }> = {
  'tts-1': { centsPerMillionChars: 1500 },
  'tts-1-hd': { centsPerMillionChars: 3000 },
};

const DEFAULT_TTS_PRICING = { centsPerMillionChars: 1500 };

/**
 * Message context structure from the API
 */
interface MessageContext {
  model?: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  /** Pre-calculated cost in cents from backend (uses real-time OpenRouter pricing) */
  costCents?: number;
  latencyMs?: number;
  ttsLatencyMs?: number;
  /** Original text content that was converted to speech (for voice messages) */
  transcription?: string;
  [key: string]: unknown;
}

/**
 * Metrics extracted from a message for display
 */
interface MessageMetrics {
  costCents: number | null;
  totalLatencyMs: number | null;
}

/**
 * Parse message context from unknown type
 */
function parseMessageContext(context: unknown): MessageContext | null {
  if (!context) return null;
  if (typeof context === 'string') {
    try {
      return JSON.parse(context) as MessageContext;
    } catch {
      return null;
    }
  }
  return context as MessageContext;
}

/**
 * Calculate TTS cost in cents based on character count
 */
function calculateTTSCost(characterCount: number): number {
  const pricing = TTS_MODEL_PRICING['tts-1'] ?? DEFAULT_TTS_PRICING;
  return (characterCount / 1_000_000) * pricing.centsPerMillionChars;
}

/**
 * Calculate API cost from message context (includes text generation + TTS if applicable)
 * Uses pre-calculated costCents from backend when available (real-time OpenRouter pricing)
 */
function calculateMessageCost(context: MessageContext | null): number | null {
  let totalCost = 0;
  let hasCost = false;

  // Use pre-calculated cost from backend when available (new messages)
  if (context?.costCents !== undefined) {
    hasCost = true;
    totalCost += context.costCents;
  } else if (context?.model && context?.usage) {
    // Fallback to client-side calculation for legacy messages
    // Note: Unknown models will show $0.00 to make missing pricing visible
    hasCost = true;
    const pricing = getTextModelPricing(context.model);
    const inputCost = (context.usage.promptTokens / 1_000_000) * pricing.inputCentsPerMillion;
    const outputCost = (context.usage.completionTokens / 1_000_000) * pricing.outputCentsPerMillion;
    totalCost += inputCost + outputCost;
  }

  // Add TTS cost if this is a voice message
  if (context?.ttsLatencyMs && context?.transcription) {
    hasCost = true;
    totalCost += calculateTTSCost(context.transcription.length);
  }

  if (!hasCost) return null;

  return Math.round(totalCost * 100) / 100; // Round to 2 decimal places
}

/**
 * Extract metrics from a message for display in the message bubble
 */
function extractMessageMetrics(context: unknown): MessageMetrics {
  const parsedContext = parseMessageContext(context);

  return {
    costCents: calculateMessageCost(parsedContext),
    totalLatencyMs: parsedContext?.latencyMs ?? null,
  };
}

/**
 * Format cost in cents to a display string.
 * Always shows 4 decimal places for individual costs.
 */
function formatCost(costCents: number | null): string {
  if (costCents === null) return '';
  return `$${(costCents / 100).toFixed(4)}`;
}

/**
 * Format latency in milliseconds to a display string
 */
function formatLatency(latencyMs: number | null): string {
  if (latencyMs === null) return '';
  if (latencyMs < 1000) {
    return `${Math.round(latencyMs)}ms`;
  }
  return `${(latencyMs / 1000).toFixed(1)}s`;
}

/**
 * Message type from the messageHistory API
 */
interface AdminMessage {
  id: string;
  direction: string;
  messageType: string;
  content: string;
  platform: string;
  platformMessageId: string | null;
  context: unknown;
  status: string | null;
  isHidden: boolean | null;
  createdAt: string | null;
}

/**
 * MessageCenter component for viewing and sending messages.
 * Displays conversation history in a chat-like interface with infinite scroll.
 */
function MessageCenter({ userId }: { userId: string }) {
  const utils = trpc.useUtils();
  const [newMessage, setNewMessage] = useState('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [allMessages, setAllMessages] = useState<AdminMessage[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<IntrospectableMessage | null>(null);
  const processedCursorsRef = useRef<Set<string | undefined>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = useRef<number>(0);

  const messagesQuery = trpc.users.messageHistory.useQuery(
    { userId, limit: 50, cursor },
    { enabled: !!userId }
  );

  const sendMessageMutation = trpc.users.sendMessage.useMutation({
    onSuccess: () => {
      setNewMessage('');
      // Reset pagination state and refetch from start
      processedCursorsRef.current.clear();
      setCursor(undefined);
      setAllMessages([]);
      utils.users.messageHistory.invalidate({ userId });
      utils.users.get.invalidate({ id: userId });
    },
  });

  // Process data when it changes - accumulate messages for pagination
  useEffect(() => {
    if (messagesQuery.data && !processedCursorsRef.current.has(cursor)) {
      // Save scroll position before updating messages (for scroll restoration)
      if (messagesContainerRef.current && cursor !== undefined) {
        prevScrollHeightRef.current = messagesContainerRef.current.scrollHeight;
      }

      processedCursorsRef.current.add(cursor);

      // API returns messages in desc order (newest first)
      // Reverse them so oldest is first for display
      const reversedMessages = [...messagesQuery.data.messages].reverse();

      if (cursor === undefined) {
        // Initial load - set messages directly
        setAllMessages(reversedMessages);
      } else {
        // Loading more (older messages) - prepend to existing messages
        setAllMessages((prev) => [...reversedMessages, ...prev]);
      }
    }
  }, [messagesQuery.data, cursor]);

  // Restore scroll position after loading older messages
  useEffect(() => {
    if (messagesContainerRef.current && prevScrollHeightRef.current > 0) {
      const newScrollHeight = messagesContainerRef.current.scrollHeight;
      const scrollDiff = newScrollHeight - prevScrollHeightRef.current;
      if (scrollDiff > 0) {
        messagesContainerRef.current.scrollTop += scrollDiff;
      }
      prevScrollHeightRef.current = 0;
    }
  }, [allMessages]);

  // Scroll to bottom on initial load
  useEffect(() => {
    if (messagesEndRef.current && cursor === undefined && allMessages.length > 0) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [allMessages, cursor]);

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;
    sendMessageMutation.mutate({ userId, content: newMessage.trim() });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Pagination state
  const hasMore = messagesQuery.data?.pagination?.hasMore ?? false;
  const nextCursor = messagesQuery.data?.pagination?.nextCursor ?? null;

  // Handle loading older messages
  const handleLoadOlder = useCallback(() => {
    if (nextCursor && !messagesQuery.isFetching) {
      setCursor(nextCursor);
    }
  }, [nextCursor, messagesQuery.isFetching]);

  // Sort all accumulated messages chronologically (oldest first)
  const sortedMessages = [...allMessages].sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return dateA - dateB;
  });

  return (
    <div style={messageCenterStyles.container}>
      <h3 style={styles.sectionTitle}>Message Center</h3>

      {messagesQuery.isLoading && allMessages.length === 0 ? (
        <div style={messageCenterStyles.loadingState}>Loading messages...</div>
      ) : messagesQuery.error ? (
        <div style={messageCenterStyles.errorState}>
          Error loading messages: {messagesQuery.error.message}
        </div>
      ) : (
        <>
          <div ref={messagesContainerRef} style={messageCenterStyles.messagesContainer}>
            {/* Load Older Messages button - shown at top when more messages are available */}
            {hasMore && (
              <div style={messageCenterStyles.loadMoreContainer}>
                <button
                  onClick={handleLoadOlder}
                  disabled={messagesQuery.isFetching}
                  style={{
                    ...messageCenterStyles.loadMoreButton,
                    ...(messagesQuery.isFetching ? messageCenterStyles.loadMoreButtonDisabled : {}),
                  }}
                >
                  {messagesQuery.isFetching ? 'Loading...' : 'Load Older Messages'}
                </button>
              </div>
            )}

            {sortedMessages.length === 0 ? (
              <div style={messageCenterStyles.emptyState}>
                No messages yet. Send a message to start the conversation.
              </div>
            ) : (
              sortedMessages.map((message) => {
                const isHidden = message.isHidden;
                const isOutbound = message.direction === 'outbound';
                const metrics = isOutbound && !isHidden ? extractMessageMetrics(message.context) : null;
                const handleMessageClick = () => {
                  const introspectableMessage: IntrospectableMessage = {
                    id: message.id,
                    direction: message.direction,
                    messageType: message.messageType,
                    content: message.content,
                    createdAt: message.createdAt,
                    context: message.context as IntrospectableMessage['context'],
                    status: message.status,
                  };
                  setSelectedMessage(introspectableMessage);
                };
                return (
                <div
                  key={message.id}
                  onClick={handleMessageClick}
                  style={{
                    ...messageCenterStyles.messageBubble,
                    ...messageCenterStyles.clickableMessage,
                    ...(isHidden
                      ? messageCenterStyles.hiddenMessage
                      : isOutbound
                        ? messageCenterStyles.outboundMessage
                        : messageCenterStyles.inboundMessage),
                  }}
                >
                  {isHidden && (
                    <div style={messageCenterStyles.hiddenBadge}>System Context</div>
                  )}
                  <div style={messageCenterStyles.messageContent}>
                    {message.content}
                  </div>
                  {/* API Metrics for outbound (bot) messages */}
                  {metrics && (metrics.costCents !== null || metrics.totalLatencyMs !== null) && (
                    <div style={messageCenterStyles.metricsRow}>
                      {metrics.costCents !== null && (
                        <span style={messageCenterStyles.metricBadge} title="API Cost">
                          {formatCost(metrics.costCents)}
                        </span>
                      )}
                      {metrics.totalLatencyMs !== null && (
                        <span style={messageCenterStyles.metricBadge} title="Generation Time">
                          {formatLatency(metrics.totalLatencyMs)}
                        </span>
                      )}
                    </div>
                  )}
                  <div
                    style={{
                      ...messageCenterStyles.messageTime,
                      ...(isHidden
                        ? messageCenterStyles.hiddenTime
                        : isOutbound
                          ? messageCenterStyles.outboundTime
                          : messageCenterStyles.inboundTime),
                    }}
                  >
                    {message.createdAt
                      ? new Date(message.createdAt).toLocaleString()
                      : 'Unknown'}
                    {message.status && message.status !== 'sent' && (
                      <span style={messageCenterStyles.messageStatus}>
                        {' '}• {message.status}
                      </span>
                    )}
                  </div>
                </div>
              );})
            )}
            <div ref={messagesEndRef} />
          </div>

          <div style={messageCenterStyles.inputContainer}>
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              style={messageCenterStyles.textarea}
              rows={2}
              disabled={sendMessageMutation.isPending}
            />
            <button
              onClick={handleSendMessage}
              style={{
                ...messageCenterStyles.sendButton,
                ...(sendMessageMutation.isPending || !newMessage.trim()
                  ? messageCenterStyles.sendButtonDisabled
                  : {}),
              }}
              disabled={sendMessageMutation.isPending || !newMessage.trim()}
            >
              {sendMessageMutation.isPending ? 'Sending...' : 'Send'}
            </button>
          </div>

          {sendMessageMutation.error && (
            <div style={messageCenterStyles.sendError}>
              Error sending message: {sendMessageMutation.error.message}
            </div>
          )}
        </>
      )}

      <MessageIntrospectionModal
        isOpen={selectedMessage !== null}
        onClose={() => setSelectedMessage(null)}
        message={selectedMessage}
      />
    </div>
  );
}

const messageCenterStyles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '75vh',
    minHeight: '500px',
    maxHeight: '900px',
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    marginBottom: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  loadingState: {
    padding: '40px',
    textAlign: 'center',
    color: '#64748b',
  },
  errorState: {
    padding: '16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
  },
  emptyState: {
    padding: '40px',
    textAlign: 'center',
    color: '#94a3b8',
    fontSize: '14px',
  },
  messageBubble: {
    maxWidth: '70%',
    padding: '12px 16px',
    borderRadius: '12px',
    wordWrap: 'break-word',
  },
  clickableMessage: {
    cursor: 'pointer',
    transition: 'opacity 0.15s ease',
  },
  inboundMessage: {
    alignSelf: 'flex-start',
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
  },
  outboundMessage: {
    alignSelf: 'flex-end',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
  },
  hiddenMessage: {
    alignSelf: 'center',
    backgroundColor: '#e5e7eb',
    color: '#6b7280',
    border: '1px dashed #9ca3af',
    borderRadius: '8px',
    maxWidth: '90%',
  },
  hiddenBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    backgroundColor: '#9ca3af',
    color: '#ffffff',
    fontSize: '10px',
    fontWeight: 600,
    borderRadius: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '6px',
  },
  hiddenTime: {
    color: '#9ca3af',
  },
  messageContent: {
    fontSize: '14px',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
  },
  messageTime: {
    fontSize: '11px',
    marginTop: '6px',
  },
  inboundTime: {
    color: '#94a3b8',
  },
  outboundTime: {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  messageStatus: {
    textTransform: 'capitalize',
  },
  metricsRow: {
    display: 'flex',
    gap: '6px',
    marginTop: '8px',
    marginBottom: '2px',
    flexWrap: 'wrap',
  },
  metricBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 6px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 500,
    letterSpacing: '0.3px',
  },
  inputContainer: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-end',
  },
  textarea: {
    flex: 1,
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    resize: 'none',
    fontFamily: 'inherit',
  },
  sendButton: {
    padding: '12px 24px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  sendButtonDisabled: {
    backgroundColor: '#94a3b8',
    cursor: 'not-allowed',
  },
  sendError: {
    marginTop: '8px',
    padding: '12px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    fontSize: '14px',
  },
  loadMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  loadMoreButton: {
    padding: '8px 16px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: '9999px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  loadMoreButtonDisabled: {
    backgroundColor: '#e2e8f0',
    color: '#94a3b8',
    cursor: 'not-allowed',
  },
};

/**
 * StatusCard component for displaying status metrics.
 * Used in the status cards grid (Status, Engagement Depth, Avg Response).
 */
function StatusCard({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={statusCardStyles.card}>
      <p style={statusCardStyles.label}>{label}</p>
      {children}
    </div>
  );
}

const statusCardStyles: Record<string, React.CSSProperties> = {
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '14px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
  },
  label: {
    fontSize: '11px',
    color: '#9B9285',
    marginBottom: '4px',
    margin: 0,
  },
};

/**
 * BehaviorMatrix component displaying a 2x2 grid showing user behavior patterns.
 * Shows quadrants for different engagement/response characteristics with an active indicator.
 */
function BehaviorMatrix({ activeQuadrant = 'high-fast' }: { activeQuadrant?: 'high-fast' | 'high-slow' | 'low-fast' | 'low-slow' }) {
  const quadrants = [
    { id: 'high-fast', label: 'High Engagement\nFast Response', position: 'top-left' },
    { id: 'high-slow', label: 'High Engagement\nSlow Response', position: 'top-right' },
    { id: 'low-fast', label: 'Low Engagement\nFast Response', position: 'bottom-left' },
    { id: 'low-slow', label: 'Low Engagement\nSlow Response', position: 'bottom-right' },
  ];

  return (
    <div style={behaviorMatrixStyles.container}>
      <h3 style={styles.sectionTitle}>Behavior Matrix</h3>
      <div style={behaviorMatrixStyles.matrix}>
        {/* Y-axis label */}
        <div style={behaviorMatrixStyles.yAxisLabel}>
          <span>Engagement</span>
        </div>
        {/* Grid container */}
        <div style={behaviorMatrixStyles.gridContainer}>
          {/* Axis labels */}
          <div style={behaviorMatrixStyles.axisLabels}>
            <span style={behaviorMatrixStyles.axisLabelTop}>High</span>
            <span style={behaviorMatrixStyles.axisLabelBottom}>Low</span>
          </div>
          {/* Matrix grid */}
          <div style={behaviorMatrixStyles.grid}>
            {quadrants.map((quadrant) => (
              <div
                key={quadrant.id}
                style={{
                  ...behaviorMatrixStyles.quadrant,
                  ...(activeQuadrant === quadrant.id ? behaviorMatrixStyles.activeQuadrant : {}),
                }}
              >
                <span style={{
                  ...behaviorMatrixStyles.quadrantLabel,
                  ...(activeQuadrant === quadrant.id ? behaviorMatrixStyles.activeQuadrantLabel : {}),
                }}>
                  {quadrant.label.split('\n').map((line, idx) => (
                    <span key={idx}>
                      {line}
                      {idx === 0 && <br />}
                    </span>
                  ))}
                </span>
                {activeQuadrant === quadrant.id && (
                  <div style={behaviorMatrixStyles.activeIndicator}>
                    <div style={behaviorMatrixStyles.activeDot} />
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* X-axis label */}
          <div style={behaviorMatrixStyles.xAxisLabels}>
            <span style={behaviorMatrixStyles.xAxisLabelLeft}>Fast</span>
            <span style={behaviorMatrixStyles.xAxisLabelRight}>Slow</span>
          </div>
          <div style={behaviorMatrixStyles.xAxisTitle}>
            <span>Response Time</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const behaviorMatrixStyles: Record<string, React.CSSProperties> = {
  container: {
    marginBottom: '24px',
  },
  matrix: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  yAxisLabel: {
    writingMode: 'vertical-rl',
    transform: 'rotate(180deg)',
    fontSize: '11px',
    color: '#9B9285',
    fontWeight: '500',
    textAlign: 'center',
  },
  gridContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  axisLabels: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    left: '-40px',
    top: '0',
    bottom: '0',
    justifyContent: 'space-between',
    paddingTop: '20px',
    paddingBottom: '20px',
  },
  axisLabelTop: {
    fontSize: '10px',
    color: '#9B9285',
  },
  axisLabelBottom: {
    fontSize: '10px',
    color: '#9B9285',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr 1fr',
    gap: '8px',
    aspectRatio: '2 / 1.5',
    minHeight: '160px',
  },
  quadrant: {
    background: 'rgba(93, 86, 78, 0.04)',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    position: 'relative',
    transition: 'all 0.2s ease',
    border: '2px solid transparent',
  },
  activeQuadrant: {
    background: 'rgba(90, 138, 94, 0.1)',
    border: '2px solid #5A8A5E',
  },
  quadrantLabel: {
    fontSize: '11px',
    color: '#9B9285',
    textAlign: 'center',
    lineHeight: '1.4',
  },
  activeQuadrantLabel: {
    color: '#5A8A5E',
    fontWeight: '600',
  },
  activeIndicator: {
    position: 'absolute',
    top: '8px',
    right: '8px',
  },
  activeDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#5A8A5E',
  },
  xAxisLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingLeft: '25%',
    paddingRight: '25%',
    marginTop: '8px',
  },
  xAxisLabelLeft: {
    fontSize: '10px',
    color: '#9B9285',
  },
  xAxisLabelRight: {
    fontSize: '10px',
    color: '#9B9285',
  },
  xAxisTitle: {
    textAlign: 'center',
    marginTop: '4px',
    fontSize: '11px',
    color: '#9B9285',
    fontWeight: '500',
  },
};

/**
 * Schedule presets for user communication schedule.
 * Maps to the SCHEDULE_PRESETS in daily-message.ts
 */
type SchedulePreset = 'daily' | 'weekdays' | 'weekends';

const SCHEDULE_PRESET_CONFIG: Record<SchedulePreset, {
  label: string;
  description: string;
  days: string[];
}> = {
  daily: {
    label: 'Daily',
    description: 'Messages every day of the week',
    days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
  },
  weekdays: {
    label: 'Weekdays',
    description: 'Messages Monday through Friday only',
    days: ['mon', 'tue', 'wed', 'thu', 'fri'],
  },
  weekends: {
    label: 'Weekends',
    description: 'Messages Saturday and Sunday only',
    days: ['sat', 'sun'],
  },
};

/**
 * Extracts the schedule preset from customData.
 * Returns the schedule preset string or null if not set.
 */
function extractSchedulePreset(customData: unknown): SchedulePreset | null {
  if (!customData) return null;

  let data = customData;
  if (typeof customData === 'string') {
    try {
      data = JSON.parse(customData);
    } catch {
      return null;
    }
  }

  if (typeof data === 'object' && data !== null && 'schedule' in data) {
    const schedule = (data as Record<string, unknown>).schedule;
    if (typeof schedule === 'string' && ['daily', 'weekdays', 'weekends'].includes(schedule)) {
      return schedule as SchedulePreset;
    }
  }

  return null;
}

/**
 * Extracts the pausedDailyUpdates flag from customData.
 * Returns true if paused, false otherwise.
 */
function extractPausedDailyUpdates(customData: unknown): boolean {
  if (!customData) return false;

  let data = customData;
  if (typeof customData === 'string') {
    try {
      data = JSON.parse(customData);
    } catch {
      return false;
    }
  }

  if (typeof data === 'object' && data !== null && 'pausedDailyUpdates' in data) {
    return (data as Record<string, unknown>).pausedDailyUpdates === true;
  }

  return false;
}

function parseCustomDataObject(customData: unknown): Record<string, unknown> | null {
  if (customData === null || customData === undefined) return null;

  if (typeof customData === 'string') {
    try {
      const parsed = JSON.parse(customData) as unknown;
      return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
        ? parsed as Record<string, unknown>
        : null;
    } catch {
      return null;
    }
  }

  return typeof customData === 'object' && customData !== null && !Array.isArray(customData)
    ? customData as Record<string, unknown>
    : null;
}

function formatCustomDataForDisplay(customData: unknown): {
  text: string;
  isJson: boolean;
} | null {
  if (customData === null || customData === undefined) {
    return null;
  }

  if (typeof customData === 'string') {
    const trimmed = customData.trim();
    if (!trimmed) {
      return null;
    }

    try {
      return {
        text: JSON.stringify(JSON.parse(trimmed), null, 2),
        isJson: true,
      };
    } catch {
      return {
        text: trimmed,
        isJson: false,
      };
    }
  }

  try {
    return {
      text: JSON.stringify(customData, null, 2),
      isJson: true,
    };
  } catch {
    return null;
  }
}

function formatCustomDataForEditor(customData: unknown): string {
  const parsed = parseCustomDataObject(customData);
  if (parsed) {
    return JSON.stringify(parsed, null, 2);
  }

  if (typeof customData === 'string') {
    return customData;
  }

  if (customData !== null && customData !== undefined) {
    try {
      return JSON.stringify(customData, null, 2);
    } catch {
      return '';
    }
  }

  return '';
}

function hasLegacyNonObjectCustomData(customData: unknown): boolean {
  if (customData === null || customData === undefined) {
    return false;
  }

  if (parseCustomDataObject(customData)) {
    return false;
  }

  if (typeof customData === 'string') {
    return customData.trim().length > 0;
  }

  try {
    return JSON.stringify(customData).trim().length > 0;
  } catch {
    return true;
  }
}

function parseCustomDataEditorValue(customDataText: string): {
  customData: Record<string, unknown> | null;
  error: string | null;
} {
  const trimmed = customDataText.trim();
  if (!trimmed) {
    return { customData: null, error: null };
  }

  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return {
        customData: null,
        error: 'Custom Data must be a valid JSON object.',
      };
    }

    return {
      customData: parsed as Record<string, unknown>,
      error: null,
    };
  } catch {
    return {
      customData: null,
      error: 'Custom Data must be valid JSON.',
    };
  }
}

function sortJsonValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortJsonValue);
  }

  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((accumulator, key) => {
        accumulator[key] = sortJsonValue((value as Record<string, unknown>)[key]);
        return accumulator;
      }, {});
  }

  return value;
}

function normalizeCustomDataText(customDataText: string): {
  normalized: string;
  customData: Record<string, unknown> | null;
  error: string | null;
} {
  const parsed = parseCustomDataEditorValue(customDataText);
  if (parsed.error) {
    return {
      normalized: `__invalid__:${customDataText.trim()}`,
      customData: null,
      error: parsed.error,
    };
  }

  return {
    normalized: JSON.stringify(sortJsonValue(parsed.customData)),
    customData: parsed.customData,
    error: null,
  };
}

const srOnlyStyles = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

async function copyTextToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'absolute';
  textArea.style.left = '-9999px';
  document.body.appendChild(textArea);
  textArea.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(textArea);
  if (!copied) {
    throw new Error('Fallback copy command failed.');
  }
}

function supportsClipboardCopy(): boolean {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    return true;
  }

  if (typeof document === 'undefined') {
    return false;
  }

  if (typeof document.queryCommandSupported === 'function') {
    return document.queryCommandSupported('copy');
  }

  return typeof document.execCommand === 'function';
}

function useTimedStatus<TStatus extends string>(resetAfterMs: number, deps: ReadonlyArray<unknown>) {
  const [status, setStatus] = useState<TStatus | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const scheduleReset = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      setStatus(null);
      timeoutRef.current = null;
    }, resetAfterMs);
  }, [resetAfterMs]);

  useEffect(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setStatus(null);
  }, deps);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    status,
    setStatus,
    scheduleReset,
  };
}

function extractCurrentStreakCount(customData: unknown): number | null {
  const data = parseCustomDataObject(customData);
  if (!data) return null;

  const currentStreakCount = data.currentStreakCount;
  return typeof currentStreakCount === 'number' ? currentStreakCount : null;
}

function extractStreakLastActivityAt(customData: unknown): string | null {
  const data = parseCustomDataObject(customData);
  if (!data) return null;

  const lastActivityAt = data.lastActivityAt;
  return typeof lastActivityAt === 'string' ? lastActivityAt : null;
}

/**
 * SchedulePresetEditor component for editing user's communication schedule preset.
 * Displays three preset options (Daily, Weekdays, Weekends) and triggers backend mutation on change.
 */
function SchedulePresetEditor({
  currentPreset,
  onUpdate,
  isUpdating,
  error,
}: {
  currentPreset: SchedulePreset | null;
  onUpdate: (preset: SchedulePreset) => void;
  isUpdating: boolean;
  error: string | null;
}) {
  const effectivePreset = currentPreset || 'daily';

  return (
    <div style={scheduleEditorStyles.container}>
      <h3 style={styles.sectionTitle}>Communication Schedule</h3>
      <p style={scheduleEditorStyles.description}>
        Choose when this user receives daily messages from the bot.
      </p>

      <div style={scheduleEditorStyles.presetsContainer}>
        {(Object.keys(SCHEDULE_PRESET_CONFIG) as SchedulePreset[]).map((preset) => {
          const config = SCHEDULE_PRESET_CONFIG[preset];
          const isSelected = effectivePreset === preset;

          return (
            <button
              key={preset}
              onClick={() => onUpdate(preset)}
              disabled={isUpdating}
              style={{
                ...scheduleEditorStyles.presetButton,
                ...(isSelected ? scheduleEditorStyles.presetButtonSelected : {}),
                ...(isUpdating ? scheduleEditorStyles.presetButtonDisabled : {}),
              }}
            >
              <div style={scheduleEditorStyles.presetHeader}>
                <span style={{
                  ...scheduleEditorStyles.presetLabel,
                  ...(isSelected ? scheduleEditorStyles.presetLabelSelected : {}),
                }}>
                  {config.label}
                </span>
                {isSelected && (
                  <span style={scheduleEditorStyles.checkmark}>✓</span>
                )}
              </div>
              <span style={{
                ...scheduleEditorStyles.presetDescription,
                ...(isSelected ? scheduleEditorStyles.presetDescriptionSelected : {}),
              }}>
                {config.description}
              </span>
              <div style={scheduleEditorStyles.daysPreview}>
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((dayLabel, idx) => {
                  const dayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
                  const isDayEnabled = config.days.includes(dayKeys[idx]);
                  return (
                    <span
                      key={idx}
                      style={{
                        ...scheduleEditorStyles.dayIndicator,
                        ...(isDayEnabled
                          ? isSelected
                            ? scheduleEditorStyles.dayIndicatorActiveSelected
                            : scheduleEditorStyles.dayIndicatorActive
                          : scheduleEditorStyles.dayIndicatorInactive),
                      }}
                    >
                      {dayLabel}
                    </span>
                  );
                })}
              </div>
            </button>
          );
        })}
      </div>

      {isUpdating && (
        <div style={scheduleEditorStyles.updatingMessage}>
          Updating schedule...
        </div>
      )}

      {error && (
        <div style={scheduleEditorStyles.errorMessage}>
          Error: {error}
        </div>
      )}
    </div>
  );
}

/**
 * PauseDailyUpdatesToggle component for pausing/resuming daily updates for a user.
 * When paused, the user will not receive automated daily messages.
 */
function PauseDailyUpdatesToggle({
  isPaused,
  onToggle,
  isUpdating,
  error,
}: {
  isPaused: boolean;
  onToggle: (paused: boolean) => void;
  isUpdating: boolean;
  error: string | null;
}) {
  return (
    <div style={pauseToggleStyles.container}>
      <div style={pauseToggleStyles.header}>
        <div style={pauseToggleStyles.labelContainer}>
          <span style={pauseToggleStyles.label}>Pause Daily Updates</span>
          <span style={pauseToggleStyles.description}>
            When enabled, this user will not receive automated daily messages
          </span>
        </div>
        <button
          onClick={() => onToggle(!isPaused)}
          disabled={isUpdating}
          style={{
            ...pauseToggleStyles.toggleButton,
            ...(isPaused ? pauseToggleStyles.toggleButtonActive : pauseToggleStyles.toggleButtonInactive),
            ...(isUpdating ? pauseToggleStyles.toggleButtonDisabled : {}),
          }}
        >
          <span
            style={{
              ...pauseToggleStyles.toggleKnob,
              ...(isPaused ? pauseToggleStyles.toggleKnobActive : pauseToggleStyles.toggleKnobInactive),
            }}
          />
        </button>
      </div>
      {isPaused && (
        <div style={pauseToggleStyles.statusMessage}>
          <span style={pauseToggleStyles.statusIcon}>⏸</span>
          Daily updates are currently paused for this user
        </div>
      )}
      {isUpdating && (
        <div style={pauseToggleStyles.updatingMessage}>
          Updating...
        </div>
      )}
      {error && (
        <div style={pauseToggleStyles.errorMessage}>
          Error: {error}
        </div>
      )}
    </div>
  );
}

/**
 * Extracts phased journey progress from customData.
 */
function extractPhasedJourneyProgress(customData: unknown): {
  currentPhaseId: string;
  currentPhasePoints: number;
  phaseHistory: Array<{ phaseId: string; completedAt: string; totalPoints: number }>;
  lastEvaluationDate: string | null;
  journeyComplete?: boolean;
} | null {
  const data = parseCustomDataObject(customData);
  if (!data) return null;

  const progress = data.phasedJourneyProgress;
  if (!progress || typeof progress !== 'object') return null;

  const p = progress as Record<string, unknown>;
  if (typeof p.currentPhaseId !== 'string' || typeof p.currentPhasePoints !== 'number') return null;

  return {
    currentPhaseId: p.currentPhaseId as string,
    currentPhasePoints: p.currentPhasePoints as number,
    phaseHistory: Array.isArray(p.phaseHistory) ? p.phaseHistory as Array<{ phaseId: string; completedAt: string; totalPoints: number }> : [],
    lastEvaluationDate: typeof p.lastEvaluationDate === 'string' ? p.lastEvaluationDate : null,
    journeyComplete: typeof p.journeyComplete === 'boolean' ? p.journeyComplete : undefined,
  };
}

/**
 * Extracts phases from bot communicationSchedule.
 */
function extractBotPhases(communicationSchedule: unknown): Array<{
  id: string;
  name: string;
  order: number;
  pointThreshold: number;
  maxDailyPoints: number;
  goals?: string[];
}> | null {
  if (!communicationSchedule || typeof communicationSchedule !== 'object') return null;
  const schedule = communicationSchedule as Record<string, unknown>;
  if (schedule.type !== 'phased_journey') return null;
  if (!Array.isArray(schedule.phases) || schedule.phases.length === 0) return null;
  return (schedule.phases as Array<Record<string, unknown>>)
    .map((p) => ({
      id: p.id as string,
      name: p.name as string,
      order: (p.order as number) ?? 0,
      pointThreshold: (p.pointThreshold as number) ?? 0,
      maxDailyPoints: (p.maxDailyPoints as number) ?? 0,
      goals: Array.isArray(p.goals) ? p.goals as string[] : undefined,
    }))
    .sort((a, b) => a.order - b.order);
}

/**
 * PhaseProgressSection - Displays a user's phased journey progress with admin controls.
 */
function PhaseProgressSection({
  userId,
  userName,
  customData,
  communicationSchedule,
}: {
  userId: string;
  userName?: string | null;
  customData: unknown;
  communicationSchedule: unknown;
}) {
  const utils = trpc.useUtils();
  const [adjustPoints, setAdjustPoints] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [selectedPhaseId, setSelectedPhaseId] = useState('');
  const [isManualEvalOpen, setIsManualEvalOpen] = useState(false);

  const adjustMutation = trpc.users.adjustPhaseProgress.useMutation({
    onSuccess: () => {
      utils.users.get.invalidate({ id: userId });
      setAdjustPoints('');
      setAdjustReason('');
      setSelectedPhaseId('');
    },
  });

  const phases = extractBotPhases(communicationSchedule);
  if (!phases) return null;

  const progress = extractPhasedJourneyProgress(customData);
  const currentPhase = progress ? phases.find((p) => p.id === progress.currentPhaseId) : null;
  const pointThreshold = currentPhase?.pointThreshold ?? 0;
  const currentPoints = progress?.currentPhasePoints ?? 0;
  const progressPercent = pointThreshold > 0 ? Math.min(100, Math.round((currentPoints / pointThreshold) * 100)) : 0;

  return (
    <div style={styles.card}>
      <h3 style={styles.sectionTitle}>Phase Progress</h3>

      {!progress ? (
        <p style={styles.emptyText}>No phase progress recorded yet.</p>
      ) : (
        <>
          {/* Current Phase */}
          <div style={phaseStyles.currentPhaseContainer}>
            <div style={phaseStyles.phaseHeader}>
              <span style={phaseStyles.phaseLabel}>Current Phase</span>
              <span style={phaseStyles.phaseName}>
                {currentPhase?.name ?? progress.currentPhaseId}
                {progress.journeyComplete && (
                  <span style={phaseStyles.completeBadge}>Journey Complete</span>
                )}
              </span>
            </div>

            {/* Points Bar */}
            <div style={phaseStyles.pointsSection}>
              <div style={phaseStyles.pointsHeader}>
                <span style={phaseStyles.pointsLabel}>Points</span>
                <span style={phaseStyles.pointsValue}>
                  {currentPoints} / {pointThreshold}
                </span>
              </div>
              <div style={phaseStyles.progressBarBg}>
                <div
                  style={{
                    ...phaseStyles.progressBarFill,
                    width: `${progressPercent}%`,
                  }}
                />
              </div>
              <span style={phaseStyles.progressPercent}>{progressPercent}%</span>
            </div>

            {progress.lastEvaluationDate && (
              <div style={phaseStyles.metaItem}>
                <span style={styles.detailLabel}>Last Evaluation</span>
                <span style={styles.detailValue}>
                  {new Date(progress.lastEvaluationDate).toLocaleString()}
                </span>
              </div>
            )}
          </div>

          {/* Phase Timeline */}
          {phases.length > 0 && (
            <div style={phaseStyles.timelineContainer}>
              <span style={phaseStyles.timelineTitle}>Phase Timeline</span>
              <div style={phaseStyles.timeline}>
                {phases.map((phase, idx) => {
                  const historyEntry = progress.phaseHistory.find((h) => h.phaseId === phase.id);
                  const isCurrent = phase.id === progress.currentPhaseId && !progress.journeyComplete;
                  const isCompleted = !!historyEntry;

                  return (
                    <div key={phase.id} style={phaseStyles.timelineItem}>
                      <div style={{
                        ...phaseStyles.timelineDot,
                        ...(isCompleted
                          ? phaseStyles.timelineDotCompleted
                          : isCurrent
                            ? phaseStyles.timelineDotCurrent
                            : phaseStyles.timelineDotPending),
                      }} />
                      {idx < phases.length - 1 && (
                        <div style={{
                          ...phaseStyles.timelineLine,
                          ...(isCompleted ? phaseStyles.timelineLineCompleted : {}),
                        }} />
                      )}
                      <div style={phaseStyles.timelineContent}>
                        <span style={{
                          ...phaseStyles.timelinePhaseName,
                          ...(isCurrent ? { fontWeight: '700' } : {}),
                        }}>
                          {phase.name}
                          {isCurrent && <span style={phaseStyles.currentIndicator}> (current)</span>}
                        </span>
                        <span style={phaseStyles.timelineThreshold}>
                          {phase.pointThreshold} pts to advance
                        </span>
                        {isCompleted && historyEntry && (
                          <span style={phaseStyles.timelineCompleted}>
                            Completed {new Date(historyEntry.completedAt).toLocaleDateString()} ({historyEntry.totalPoints} pts)
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Admin Controls */}
      <div style={phaseStyles.adminControls}>
        <span style={phaseStyles.adminControlsTitle}>Admin Actions</span>

        {/* Adjust Points */}
        <div style={phaseStyles.controlRow}>
          <div style={phaseStyles.controlGroup}>
            <label style={styles.formLabel}>Adjust Points</label>
            <div style={phaseStyles.inlineInputGroup}>
              <input
                type="number"
                value={adjustPoints}
                onChange={(e) => setAdjustPoints(e.target.value)}
                placeholder="e.g. 10 or -5"
                style={{ ...styles.formInput, width: '120px' }}
              />
              <input
                type="text"
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
                placeholder="Reason (optional)"
                style={{ ...styles.formInput, flex: 1 }}
              />
              <button
                onClick={() => {
                  const pts = parseInt(adjustPoints, 10);
                  if (isNaN(pts)) return;
                  adjustMutation.mutate({
                    userId,
                    action: 'adjust_points',
                    points: pts,
                    reason: adjustReason || undefined,
                  });
                }}
                disabled={adjustMutation.isPending || !adjustPoints}
                style={{
                  ...phaseStyles.actionButton,
                  ...(adjustMutation.isPending || !adjustPoints ? phaseStyles.actionButtonDisabled : {}),
                }}
              >
                {adjustMutation.isPending ? 'Updating...' : 'Apply'}
              </button>
            </div>
          </div>
        </div>

        {/* Set Phase */}
        <div style={phaseStyles.controlRow}>
          <div style={phaseStyles.controlGroup}>
            <label style={styles.formLabel}>Move to Phase</label>
            <div style={phaseStyles.inlineInputGroup}>
              <select
                value={selectedPhaseId}
                onChange={(e) => setSelectedPhaseId(e.target.value)}
                style={{ ...styles.formSelect, flex: 1 }}
              >
                <option value="">-- Select Phase --</option>
                {phases.map((phase) => (
                  <option key={phase.id} value={phase.id}>
                    {phase.name} (threshold: {phase.pointThreshold})
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  if (!selectedPhaseId) return;
                  const confirmed = window.confirm(
                    `Move user to phase "${phases.find((p) => p.id === selectedPhaseId)?.name}"? Points will be reset to 0.`
                  );
                  if (confirmed) {
                    adjustMutation.mutate({
                      userId,
                      action: 'set_phase',
                      phaseId: selectedPhaseId,
                      reason: adjustReason || undefined,
                    });
                  }
                }}
                disabled={adjustMutation.isPending || !selectedPhaseId}
                style={{
                  ...phaseStyles.actionButton,
                  ...(adjustMutation.isPending || !selectedPhaseId ? phaseStyles.actionButtonDisabled : {}),
                }}
              >
                Move
              </button>
            </div>
          </div>
        </div>

        {/* Reset Progress */}
        <div style={phaseStyles.controlRow}>
          <button
            onClick={() => {
              const confirmed = window.confirm(
                'Reset all phase progress for this user? This will move them back to the first phase with 0 points.'
              );
              if (confirmed) {
                adjustMutation.mutate({
                  userId,
                  action: 'reset',
                  reason: adjustReason || undefined,
                });
              }
            }}
            disabled={adjustMutation.isPending}
            style={{
              ...phaseStyles.resetButton,
              ...(adjustMutation.isPending ? phaseStyles.actionButtonDisabled : {}),
            }}
          >
            Reset Progress
          </button>
        </div>

        {/* Manual Evaluation */}
        <div style={phaseStyles.controlRow}>
          <button
            onClick={() => setIsManualEvalOpen(true)}
            style={phaseStyles.evalButton}
          >
            Evaluate Progress (Manual)
          </button>
        </div>

        {adjustMutation.error && (
          <div style={styles.formError}>
            Error: {adjustMutation.error.message}
          </div>
        )}
      </div>

      {/* Manual Evaluation Modal */}
      <ManualEvaluationModal
        isOpen={isManualEvalOpen}
        onClose={() => setIsManualEvalOpen(false)}
        onSuccess={() => utils.users.get.invalidate({ id: userId })}
        userId={userId}
        userName={userName}
      />
    </div>
  );
}

const phaseStyles: Record<string, React.CSSProperties> = {
  currentPhaseContainer: {
    marginBottom: '20px',
  },
  phaseHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '16px',
  },
  phaseLabel: {
    fontSize: '12px',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  phaseName: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  completeBadge: {
    display: 'inline-block',
    padding: '3px 10px',
    backgroundColor: '#dcfce7',
    color: '#166534',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: '500',
  },
  pointsSection: {
    marginBottom: '12px',
  },
  pointsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '6px',
  },
  pointsLabel: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#64748b',
  },
  pointsValue: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#334155',
  },
  progressBarBg: {
    width: '100%',
    height: '10px',
    backgroundColor: '#f1f5f9',
    borderRadius: '5px',
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: '#3b82f6',
    borderRadius: '5px',
    transition: 'width 0.3s ease',
  },
  progressPercent: {
    fontSize: '12px',
    color: '#94a3b8',
    marginTop: '4px',
    display: 'inline-block',
  },
  metaItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginTop: '12px',
  },
  timelineContainer: {
    marginBottom: '20px',
    paddingTop: '16px',
    borderTop: '1px solid #f1f5f9',
  },
  timelineTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#475569',
    marginBottom: '12px',
    display: 'block',
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
    position: 'relative',
  },
  timelineItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    position: 'relative',
    paddingBottom: '16px',
  },
  timelineDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    flexShrink: 0,
    marginTop: '3px',
    zIndex: 1,
  },
  timelineDotCompleted: {
    backgroundColor: '#22c55e',
  },
  timelineDotCurrent: {
    backgroundColor: '#3b82f6',
    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.2)',
  },
  timelineDotPending: {
    backgroundColor: '#e2e8f0',
  },
  timelineLine: {
    position: 'absolute',
    left: '5px',
    top: '18px',
    width: '2px',
    height: 'calc(100% - 6px)',
    backgroundColor: '#e2e8f0',
  },
  timelineLineCompleted: {
    backgroundColor: '#22c55e',
  },
  timelineContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  timelinePhaseName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#334155',
  },
  currentIndicator: {
    color: '#3b82f6',
    fontWeight: '500',
    fontSize: '12px',
  },
  timelineThreshold: {
    fontSize: '12px',
    color: '#94a3b8',
  },
  timelineCompleted: {
    fontSize: '12px',
    color: '#22c55e',
  },
  adminControls: {
    paddingTop: '16px',
    borderTop: '1px solid #f1f5f9',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  adminControlsTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#475569',
    marginBottom: '4px',
  },
  controlRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  controlGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  inlineInputGroup: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  actionButton: {
    padding: '10px 16px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  actionButtonDisabled: {
    backgroundColor: '#94a3b8',
    cursor: 'not-allowed',
  },
  resetButton: {
    padding: '10px 16px',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    alignSelf: 'flex-start',
  },
  evalButton: {
    padding: '10px 16px',
    backgroundColor: '#8b5cf6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    alignSelf: 'flex-start',
  },
};

/**
 * ScheduledMessagesCard component for displaying pending scheduled messages.
 * Shows messages that are scheduled to be sent in the future.
 */
function ScheduledMessagesCard({ userId }: { userId: string }) {
  const scheduledMessagesQuery = trpc.users.pendingScheduledMessages.useQuery(
    { userId },
    { enabled: !!userId }
  );

  const formatScheduledTime = (scheduledFor: string) => {
    const date = new Date(scheduledFor);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    let relativeTime = '';
    if (diffDays > 0) {
      relativeTime = `in ${diffDays} day${diffDays > 1 ? 's' : ''}`;
    } else if (diffHours > 0) {
      relativeTime = `in ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
    } else if (diffMins > 0) {
      relativeTime = `in ${diffMins} minute${diffMins > 1 ? 's' : ''}`;
    } else {
      relativeTime = 'any moment now';
    }

    return {
      absolute: date.toLocaleString(),
      relative: relativeTime,
    };
  };

  return (
    <div style={scheduledMessagesStyles.container}>
      <h3 style={styles.sectionTitle}>Scheduled Messages</h3>

      {scheduledMessagesQuery.isLoading ? (
        <div style={scheduledMessagesStyles.loadingState}>Loading scheduled messages...</div>
      ) : scheduledMessagesQuery.error ? (
        <div style={scheduledMessagesStyles.errorState}>
          Error loading scheduled messages: {scheduledMessagesQuery.error.message}
        </div>
      ) : scheduledMessagesQuery.data?.messages.length === 0 ? (
        <div style={scheduledMessagesStyles.emptyState}>
          No scheduled messages pending for this user.
        </div>
      ) : (
        <div style={scheduledMessagesStyles.messagesList}>
          {scheduledMessagesQuery.data?.messages.map((message) => {
            const time = formatScheduledTime(message.scheduledFor);
            return (
              <div key={message.id} style={scheduledMessagesStyles.messageItem}>
                <div style={scheduledMessagesStyles.messageHeader}>
                  <span style={scheduledMessagesStyles.scheduledBadge}>Scheduled</span>
                  <span style={scheduledMessagesStyles.scheduledTime}>
                    {time.relative}
                  </span>
                </div>
                <div style={scheduledMessagesStyles.messageContent}>
                  {message.message}
                </div>
                <div style={scheduledMessagesStyles.messageFooter}>
                  <span style={scheduledMessagesStyles.absoluteTime}>
                    {time.absolute}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const scheduledMessagesStyles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  loadingState: {
    padding: '24px',
    textAlign: 'center',
    color: '#64748b',
    fontSize: '14px',
  },
  errorState: {
    padding: '16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    fontSize: '14px',
  },
  emptyState: {
    padding: '24px',
    textAlign: 'center',
    color: '#94a3b8',
    fontSize: '14px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
  },
  messagesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  messageItem: {
    padding: '16px',
    backgroundColor: '#fffbeb',
    borderRadius: '8px',
    border: '1px solid #fde68a',
  },
  messageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  scheduledBadge: {
    display: 'inline-block',
    padding: '4px 8px',
    backgroundColor: '#fef3c7',
    color: '#92400e',
    fontSize: '11px',
    fontWeight: 600,
    borderRadius: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  scheduledTime: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#92400e',
  },
  messageContent: {
    fontSize: '14px',
    lineHeight: '1.5',
    color: '#334155',
    whiteSpace: 'pre-wrap',
  },
  messageFooter: {
    marginTop: '8px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  absoluteTime: {
    fontSize: '12px',
    color: '#94a3b8',
  },
};

const pauseToggleStyles: Record<string, React.CSSProperties> = {
  container: {
    marginBottom: '24px',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '12px',
    border: '1px solid rgba(93, 86, 78, 0.1)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  labelContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  label: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#3D3A36',
  },
  description: {
    fontSize: '13px',
    color: '#9B9285',
  },
  toggleButton: {
    width: '52px',
    height: '28px',
    borderRadius: '14px',
    border: 'none',
    padding: '2px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
  },
  toggleButtonActive: {
    backgroundColor: '#f59e0b',
  },
  toggleButtonInactive: {
    backgroundColor: '#e2e8f0',
  },
  toggleButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  toggleKnob: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#ffffff',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
    transition: 'transform 0.2s ease',
    display: 'block',
  },
  toggleKnobActive: {
    transform: 'translateX(24px)',
  },
  toggleKnobInactive: {
    transform: 'translateX(0)',
  },
  statusMessage: {
    marginTop: '12px',
    padding: '10px 12px',
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    color: '#92400e',
    borderRadius: '8px',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statusIcon: {
    fontSize: '16px',
  },
  updatingMessage: {
    marginTop: '12px',
    padding: '10px 12px',
    backgroundColor: '#f0f9ff',
    color: '#0369a1',
    borderRadius: '8px',
    fontSize: '14px',
  },
  errorMessage: {
    marginTop: '12px',
    padding: '10px 12px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    fontSize: '14px',
  },
};

const scheduleEditorStyles: Record<string, React.CSSProperties> = {
  container: {
    marginBottom: '24px',
  },
  description: {
    fontSize: '14px',
    color: '#64748b',
    marginBottom: '20px',
    margin: 0,
    marginBlockEnd: '20px',
  },
  presetsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
  },
  presetButton: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px',
    borderRadius: '12px',
    border: '2px solid rgba(93, 86, 78, 0.15)',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textAlign: 'left',
  },
  presetButtonSelected: {
    borderColor: '#5A8A5E',
    backgroundColor: 'rgba(90, 138, 94, 0.05)',
  },
  presetButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  presetHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
  },
  presetLabel: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#3D3A36',
  },
  presetLabelSelected: {
    color: '#5A8A5E',
  },
  checkmark: {
    color: '#5A8A5E',
    fontWeight: '600',
    fontSize: '16px',
  },
  presetDescription: {
    fontSize: '13px',
    color: '#9B9285',
    marginBottom: '12px',
  },
  presetDescriptionSelected: {
    color: '#5A8A5E',
  },
  daysPreview: {
    display: 'flex',
    gap: '4px',
  },
  dayIndicator: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: '600',
  },
  dayIndicatorActive: {
    backgroundColor: 'rgba(90, 138, 94, 0.2)',
    color: '#5A8A5E',
  },
  dayIndicatorActiveSelected: {
    backgroundColor: '#5A8A5E',
    color: '#ffffff',
  },
  dayIndicatorInactive: {
    backgroundColor: 'rgba(93, 86, 78, 0.08)',
    color: '#9B9285',
  },
  updatingMessage: {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: '#f0f9ff',
    color: '#0369a1',
    borderRadius: '8px',
    fontSize: '14px',
  },
  errorMessage: {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    fontSize: '14px',
  },
};

/**
 * Helper function to get color based on engagement score.
 */
function getEngagementColor(score: number): string {
  if (score > 60) return '#5A8A5E'; // Green for high engagement
  if (score > 40) return '#9B7B3C'; // Yellow/gold for medium
  return '#8B5A50'; // Red for low
}

/**
 * User detail page component.
 *
 * Displays detailed information about a specific user including:
 * - Profile data (goals, cycle info)
 * - Subscription status and management
 * - Bot information
 * - Update functionality for subscription/status
 */
export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const [isEditing, setIsEditing] = useState(false);
  const [isResetHistoryPending, setIsResetHistoryPending] = useState(false);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [isGeneratingInsights, setIsGeneratingInsights] = useState(false);
  const [selectedInactivityDay, setSelectedInactivityDay] = useState('');
  const [editForm, setEditForm] = useState({
    name: '',
    preferredMessageTime: '',
    subscriptionStatus: '',
    subscriptionExpiresAt: '',
    conversationState: '',
    churnRisk: '',
    customData: '',
  });
  const [initialCustomDataText, setInitialCustomDataText] = useState('');
  const [customDataError, setCustomDataError] = useState<string | null>(null);

  const userQuery = trpc.users.get.useQuery(
    { id: userId! },
    { enabled: !!userId }
  );

  // Fetch plans for plan management
  const plansQuery = trpc.plans.list.useQuery(
    { botId: userQuery.data?.bot?.id ?? undefined, status: 'active' },
    { enabled: !!userQuery.data?.bot?.id }
  );

  // Mutation to update user's plan
  const updatePlanMutation = trpc.users.update.useMutation({
    onSuccess: () => {
      utils.users.get.invalidate({ id: userId! });
    },
  });

  const updateMutation = trpc.users.update.useMutation({
    onSuccess: () => {
      utils.users.get.invalidate({ id: userId! });
      setInitialCustomDataText('');
      setIsEditing(false);
    },
  });

  const togglePauseMutation = trpc.users.update.useMutation({
    onSuccess: () => {
      utils.users.get.invalidate({ id: userId! });
    },
  });

  const updateScheduleMutation = trpc.users.updateSchedule.useMutation({
    onSuccess: () => {
      utils.users.get.invalidate({ id: userId! });
    },
  });

  const updatePauseDailyUpdatesMutation = trpc.users.updatePauseDailyUpdates.useMutation({
    onSuccess: () => {
      utils.users.get.invalidate({ id: userId! });
    },
  });

  const resetHistoryMutation = trpc.users.resetHistory.useMutation({
    onSuccess: () => {
      utils.users.get.invalidate({ id: userId! });
      utils.users.messageHistory.invalidate({ userId: userId! });
      setIsResetHistoryPending(false);
      window.alert('User history has been reset successfully. All messages, profile data, goals, and engagement metrics have been wiped. The user is now in a completely NEW state.');
    },
    onError: () => {
      setIsResetHistoryPending(false);
    },
  });

  const deleteUserMutation = trpc.users.delete.useMutation({
    onSuccess: () => {
      setIsDeletingUser(false);
      utils.users.list.invalidate();
      navigate('/users');
    },
    onError: () => {
      setIsDeletingUser(false);
    },
  });

  const impersonateMutation = trpc.users.impersonate.useMutation({
    onSuccess: (data: { token: string; frontendUrl: string }) => {
      setIsImpersonating(false);
      // Open the frontend with the token in a new tab
      // The frontend will pick up the token from the URL and store it
      const url = `${data.frontendUrl}/login?token=${encodeURIComponent(data.token)}`;
      window.open(url, '_blank');
    },
    onError: () => {
      setIsImpersonating(false);
    },
  });

  const insightsQuery = trpc.users.getInsights.useQuery(
    { userId: userId! },
    { enabled: !!userId }
  );

  const generateInsightsMutation = trpc.users.generateInsights.useMutation({
    onSuccess: () => {
      setIsGeneratingInsights(false);
      insightsQuery.refetch();
      utils.users.get.invalidate({ id: userId! });
    },
    onError: () => {
      setIsGeneratingInsights(false);
    },
  });

  const sendManualInactivityMessageMutation = trpc.users.sendManualInactivityMessage.useMutation({
    onSuccess: () => {
      utils.users.get.invalidate({ id: userId! });
      utils.users.messageHistory.invalidate({ userId: userId! });
      window.alert('Inactivity template check-in sent successfully.');
    },
  });

  const inactivityTemplateResetKey = [
    userId,
    userQuery.data?.bot?.id ?? '',
    JSON.stringify(userQuery.data?.bot?.inactivityMessageTemplates ?? []),
  ].join(':');

  useEffect(() => {
    setSelectedInactivityDay('');
  }, [inactivityTemplateResetKey]);

  const profileCustomDataDisplay = formatCustomDataForDisplay(userQuery.data?.profile?.customData);
  const hasLegacyCustomDataValue = hasLegacyNonObjectCustomData(userQuery.data?.profile?.customData);
  const {
    status: copyCustomDataStatus,
    setStatus: setCopyCustomDataStatus,
    scheduleReset: scheduleCopyCustomDataStatusReset,
  } = useTimedStatus<'copied' | 'error'>(2000, [userId, profileCustomDataDisplay?.text]);

  const copyCustomDataAnnouncement = copyCustomDataStatus === 'copied'
    ? 'Custom Data copied to clipboard.'
    : copyCustomDataStatus === 'error'
    ? 'Unable to copy Custom Data.'
    : '';
  const canCopyCustomData = Boolean(profileCustomDataDisplay?.text) && supportsClipboardCopy();

  const handleCopyCustomData = useCallback(async () => {
    if (!profileCustomDataDisplay) {
      setCopyCustomDataStatus('error');
      scheduleCopyCustomDataStatusReset();
      return;
    }

    try {
      await copyTextToClipboard(profileCustomDataDisplay.text);
      setCopyCustomDataStatus('copied');
      scheduleCopyCustomDataStatusReset();
    } catch {
      setCopyCustomDataStatus('error');
      scheduleCopyCustomDataStatusReset();
    }
  }, [profileCustomDataDisplay, scheduleCopyCustomDataStatusReset]);

  if (!userId) {
    return (
      <div style={styles.error}>
        Invalid user ID
      </div>
    );
  }

  if (userQuery.isLoading) {
    return <div style={styles.loading}>Loading user details...</div>;
  }

  if (userQuery.error) {
    return (
      <div style={styles.error}>
        Error loading user: {userQuery.error.message}
      </div>
    );
  }

  const user = userQuery.data;

  const inactivityTemplatesState = parseInactivityMessageTemplatesResult(
    user?.bot?.inactivityMessageTemplates
  );
  const inactivityTemplates = [...inactivityTemplatesState.templates].sort((a, b) => a.day - b.day);

  const detectedInactivityDays = getInactivityDays(user?.lastInboundMessageAt);
  const isLessThan24HoursInactive = detectedInactivityDays === 0;
  const isWhatsAppUser = user?.platform === 'whatsapp';
  const hasDetectedTemplateMatch =
    detectedInactivityDays !== null
    && inactivityTemplates.some((template) => template.day === detectedInactivityDays);
  const isInactivitySelectorDisabled =
    sendManualInactivityMessageMutation.isPending
    || !isWhatsAppUser
    || !user?.bot
    || inactivityTemplatesState.isInvalid
    || inactivityTemplates.length === 0;
  const isInactivityActionDisabled =
    isInactivitySelectorDisabled
    || (selectedInactivityDay === '' && !hasDetectedTemplateMatch);

  if (!user) {
    return (
      <div style={styles.error}>
        User not found
      </div>
    );
  }

  const handleEditClick = () => {
    const customDataText = formatCustomDataForEditor(user.profile?.customData);

    setEditForm({
      name: user.name ?? '',
      preferredMessageTime: user.preferredMessageTime ?? '',
      subscriptionStatus: user.subscriptionStatus ?? '',
      subscriptionExpiresAt: user.subscriptionExpiresAt ?? '',
      conversationState: user.conversationState ?? '',
      churnRisk: user.churnRisk ?? '',
      customData: customDataText,
    });
    setInitialCustomDataText(customDataText);
    setCustomDataError(null);
    setIsEditing(true);
  };

  const handleSave = () => {
    const updates: {
      id: string;
      name?: string;
      preferredMessageTime?: string;
      subscriptionStatus?: 'trial' | 'active' | 'expired' | 'cancelled';
      subscriptionExpiresAt?: string | null;
      conversationState?: 'new' | 'onboarding' | 'active' | 'paused';
      churnRisk?: 'low' | 'medium' | 'high';
      customData?: Record<string, unknown> | null;
    } = { id: userId };

    const initialCustomData = normalizeCustomDataText(initialCustomDataText);
    const nextCustomData = normalizeCustomDataText(editForm.customData);

    if (nextCustomData.normalized !== initialCustomData.normalized && nextCustomData.error) {
      setCustomDataError(nextCustomData.error);
      return;
    }

    if (nextCustomData.normalized !== initialCustomData.normalized) {
      updates.customData = nextCustomData.customData;
    }
    setCustomDataError(null);

    // Handle name - only include if non-empty, trim whitespace
    const trimmedName = editForm.name.trim();
    if (trimmedName) {
      updates.name = trimmedName;
    }

    // Handle preferredMessageTime - only include if non-empty (valid HH:MM format)
    const trimmedTime = editForm.preferredMessageTime.trim();
    if (trimmedTime) {
      updates.preferredMessageTime = trimmedTime;
    }

    if (editForm.subscriptionStatus) {
      updates.subscriptionStatus = editForm.subscriptionStatus as 'trial' | 'active' | 'expired' | 'cancelled';
    }
    if (editForm.subscriptionExpiresAt) {
      updates.subscriptionExpiresAt = editForm.subscriptionExpiresAt;
    } else if (editForm.subscriptionExpiresAt === '' && user.subscriptionExpiresAt) {
      updates.subscriptionExpiresAt = null;
    }
    if (editForm.conversationState) {
      updates.conversationState = editForm.conversationState as 'new' | 'onboarding' | 'active' | 'paused';
    }
    if (editForm.churnRisk) {
      updates.churnRisk = editForm.churnRisk as 'low' | 'medium' | 'high';
    }

    updateMutation.mutate(updates);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setInitialCustomDataText('');
    setCustomDataError(null);
  };

  const handleTogglePause = () => {
    const newState = user?.conversationState === 'paused' ? 'active' : 'paused';
    togglePauseMutation.mutate({
      id: userId,
      conversationState: newState,
    });
  };

  const isPaused = user?.conversationState === 'paused';

  const handleSendCheckIn = () => {
    const inactivityDaysOverride = selectedInactivityDay ? Number(selectedInactivityDay) : undefined;
    const templateMode = inactivityDaysOverride !== undefined
      ? `day ${inactivityDaysOverride}`
      : detectedInactivityDays !== null
      ? `the detected day-${detectedInactivityDays} template`
      : 'the detected inactivity template';

    const confirmed = window.confirm(
      `Send ${templateMode} to ${user?.name || 'this user'}?`
    );

    if (confirmed) {
      sendManualInactivityMessageMutation.mutate({
        userId: userId!,
        inactivityDaysOverride,
      });
    }
  };

  const handleResetHistory = () => {
    const confirmed = window.confirm(
      `Are you sure you want to reset the history for ${user?.name || 'this user'}? This will delete ALL messages, user profile, goals, and engagement metrics, resetting the user to a completely NEW state. This action is IRREVERSIBLE.`
    );
    if (confirmed) {
      setIsResetHistoryPending(true);
      resetHistoryMutation.mutate({ userId: userId! });
    }
  };

  const handleDeleteUser = () => {
    const displayName = user?.name || user?.phoneNumber || 'this user';
    const confirmed = window.confirm(
      `Completely delete ${displayName}? This permanently deletes the user record and associated messages, profile, support, scheduled jobs, metrics, and test-session data. This action cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    const typedConfirmation = window.prompt('Type DELETE to permanently delete this user.');
    if (typedConfirmation !== 'DELETE') {
      return;
    }

    setIsDeletingUser(true);
    deleteUserMutation.mutate({ userId: userId! });
  };

  const handleLoginAsUser = () => {
    const confirmed = window.confirm(
      `Login as ${user?.name || 'this user'} in the user frontend? This will open a new tab with their session.`
    );
    if (confirmed) {
      setIsImpersonating(true);
      impersonateMutation.mutate({ userId: userId! });
    }
  };

  const handleGenerateInsights = (force: boolean) => {
    const msg = force
      ? `Force-regenerate behavioral insights for ${user?.name || 'this user'}? This bypasses cooldown and message count gating.`
      : `Generate behavioral insights for ${user?.name || 'this user'}? This analyzes their recent messages.`;
    const confirmed = window.confirm(msg);
    if (confirmed) {
      setIsGeneratingInsights(true);
      generateInsightsMutation.mutate({ userId: userId!, force });
    }
  };

  const handleChangePlan = (planId: string | null) => {
    updatePlanMutation.mutate({
      id: userId!,
      subscriptionPlanId: planId,
    });
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button onClick={() => navigate('/users')} style={styles.backButton}>
          &larr; Back to Users
        </button>
        <h1 style={styles.title}>User Details</h1>
      </div>

      {/* User Header Card - Styled with gradient avatar */}
      <div style={styles.card}>
        <div style={styles.userHeader}>
          <div style={{
            ...styles.avatar,
            background: `linear-gradient(135deg, ${DEFAULT_MODULE_COLOR} 0%, ${DEFAULT_MODULE_COLOR}80 100%)`,
          }}>
            {user.name?.[0]?.toUpperCase() || '?'}
          </div>
          <div style={styles.userInfo}>
            <h2 style={styles.userName}>{user.name || 'Unknown'}</h2>
            <p style={styles.userEmail}>{user.phoneNumber || user.id}</p>
          </div>
        </div>

        {/* Status Cards Grid */}
        <div style={styles.statusCardsGrid}>
          <StatusCard label="Status">
            <span
              style={{
                ...styles.statusBadgeSmall,
                ...getStatusStyleUpdated(user.subscriptionStatus ?? ''),
              }}
            >
              {user.subscriptionStatus ?? 'Unknown'}
            </span>
          </StatusCard>
          <StatusCard label="Engagement Depth">
            <p style={{
              ...styles.engagementValue,
              color: getEngagementColor(user.engagementScore ?? 0),
            }}>
              {Math.round((user.engagementScore ?? 0) / 10)}/10
            </p>
          </StatusCard>
          <StatusCard label="Avg Response">
            <p style={styles.avgResponseValue}>
              {user.preferredMessageTime || '—'}
            </p>
          </StatusCard>
        </div>

        {/* User Details Grid */}
        <div style={styles.detailsGrid}>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Phone Number</span>
            <span style={styles.detailValue}>{user.phoneNumber || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Platform</span>
            <span style={styles.detailValue}>{user.platform || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Timezone</span>
            <span style={styles.detailValue}>{user.timezone || 'UTC'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Preferred Message Time</span>
            <span style={styles.detailValue}>{user.preferredMessageTime || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Last Active</span>
            <span style={styles.detailValue}>
              {user.lastMessageAt
                ? new Date(user.lastMessageAt).toLocaleString()
                : 'Never'}
            </span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Created At</span>
            <span style={styles.detailValue}>
              {user.createdAt
                ? new Date(user.createdAt).toLocaleString()
                : 'Unknown'}
            </span>
          </div>
        </div>
      </div>

      {/* Subscription Card - Styled with gradient background */}
      <div style={{
        ...styles.subscriptionCard,
        background: `linear-gradient(135deg, ${DEFAULT_MODULE_COLOR}15 0%, ${DEFAULT_MODULE_COLOR}05 100%)`,
      }}>
        <p style={styles.subscriptionTitle}>Subscription</p>
        <div style={styles.subscriptionContent}>
          <div>
            <p style={styles.subscriptionPlan}>
              {user.subscriptionStatus === 'trial' ? 'Trial' :
               user.subscriptionStatus === 'active' ? 'Active Plan' :
               user.subscriptionStatus === 'expired' ? 'Expired' :
               user.subscriptionStatus === 'cancelled' ? 'Cancelled' : 'Unknown'}
            </p>
            <p style={styles.subscriptionSince}>
              Since {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
            </p>
          </div>
          <div style={styles.subscriptionRight}>
            {user.subscriptionExpiresAt && (
              <>
                <p style={styles.subscriptionExpiry}>
                  {user.subscriptionStatus === 'trial' ? 'Trial ends' : 'Expires'}
                </p>
                <p style={styles.subscriptionExpiryDate}>
                  {new Date(user.subscriptionExpiresAt).toLocaleDateString()}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions Section */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Quick Actions</h3>
        <div style={styles.quickActionsContainer}>
          <div style={styles.quickActionsButtonRow}>
            <button
              onClick={handleTogglePause}
              style={{
                ...styles.quickActionButton,
                ...(isPaused ? styles.resumeButton : styles.pauseButton),
                ...(togglePauseMutation.isPending ? styles.quickActionButtonDisabled : {}),
              }}
              disabled={togglePauseMutation.isPending}
            >
              {togglePauseMutation.isPending
                ? 'Updating...'
                : isPaused
                ? 'Resume Bot'
                : 'Pause Bot'}
            </button>
            <button
              onClick={handleSendCheckIn}
              style={{
                ...styles.quickActionButton,
                ...styles.sendCheckInButton,
                ...(isInactivityActionDisabled ? styles.quickActionButtonDisabled : {}),
              }}
              disabled={isInactivityActionDisabled}
            >
              {sendManualInactivityMessageMutation.isPending ? 'Sending...' : 'Send Inactivity Check-in'}
            </button>
            <button
              onClick={handleResetHistory}
              style={{
                ...styles.quickActionButton,
                ...styles.resetHistoryButton,
                ...(isResetHistoryPending ? styles.quickActionButtonDisabled : {}),
              }}
              disabled={isResetHistoryPending}
            >
              {isResetHistoryPending ? 'Resetting...' : 'Reset History'}
            </button>
            <button
              onClick={handleDeleteUser}
              style={{
                ...styles.quickActionButton,
                ...styles.deleteUserButton,
                ...(isDeletingUser ? styles.quickActionButtonDisabled : {}),
              }}
              disabled={isDeletingUser}
            >
              {isDeletingUser ? 'Deleting...' : 'Completely Delete User'}
            </button>
            <button
              onClick={handleLoginAsUser}
              style={{
                ...styles.quickActionButton,
                ...styles.loginAsUserButton,
                ...(isImpersonating ? styles.quickActionButtonDisabled : {}),
              }}
              disabled={isImpersonating}
            >
              {isImpersonating ? 'Opening...' : 'Login as User'}
            </button>
            <button
              onClick={() => handleGenerateInsights(true)}
              style={{
                ...styles.quickActionButton,
                ...styles.generateInsightsButton,
                ...(isGeneratingInsights ? styles.quickActionButtonDisabled : {}),
              }}
              disabled={isGeneratingInsights}
            >
              {isGeneratingInsights ? 'Generating...' : 'Generate Insights'}
            </button>
          </div>
          <div style={styles.inactivityActionPanel}>
            <div style={styles.inactivityActionHeader}>
              <span style={styles.inactivityActionTitle}>Manual inactivity template</span>
              <span style={styles.inactivityActionMeta}>
                {isLessThan24HoursInactive
                  ? 'Less than 24 hours inactive'
                  : detectedInactivityDays !== null
                  ? `Detected day ${detectedInactivityDays}`
                  : 'No detected inactivity day'}
              </span>
            </div>
            <div style={styles.inactivityActionControls}>
              <select
                value={selectedInactivityDay}
                onChange={(e) => setSelectedInactivityDay(e.target.value)}
                style={styles.inactivityActionSelect}
                disabled={isInactivitySelectorDisabled}
              >
                <option value="">
                  Auto-detect exact day
                </option>
                {inactivityTemplates.map((template) => (
                  <option
                    key={`${template.day}-${template.templateName}-${template.languageCode ?? 'en'}`}
                    value={String(template.day)}
                  >
                    Day {template.day}: {template.templateName}
                  </option>
                ))}
              </select>
              <span style={styles.inactivityActionHint}>
                {!isWhatsAppUser
                  ? 'This action is only available for WhatsApp users.'
                  : !user?.bot
                  ? 'This user must be assigned to a bot before an inactivity template can be sent.'
                  : inactivityTemplatesState.isInvalid
                  ? 'This bot’s inactivity template config is invalid. Fix the stored JSON before sending a manual inactivity template.'
                  : inactivityTemplates.length === 0
                  ? 'No inactivity templates are configured on this user’s bot.'
                  : selectedInactivityDay === '' && isLessThan24HoursInactive
                  ? 'Auto-detect is unavailable because this user has been inactive for less than 24 hours. Choose an exact configured day override to send one manually.'
                  : selectedInactivityDay === '' && detectedInactivityDays !== null && !hasDetectedTemplateMatch
                  ? `No template is configured for detected day ${detectedInactivityDays}. Choose an exact configured day override to send one manually.`
                  : selectedInactivityDay === '' && detectedInactivityDays === null
                  ? 'Auto-detect is unavailable because this user does not have a valid inbound message timestamp. Choose an exact configured day override to send one manually.'
                  : selectedInactivityDay
                  ? `Override enabled: send the configured day-${selectedInactivityDay} template.`
                  : 'Auto-detect uses the user’s exact days since their last inbound message and requires a matching configured template.'}
              </span>
            </div>
            <div style={styles.inactivityTemplateList}>
              {inactivityTemplates.length > 0 ? (
                inactivityTemplates.map((template) => (
                  <span
                    key={`${template.day}-${template.templateName}-${template.languageCode ?? 'en'}-tag`}
                    style={styles.inactivityTemplateTag}
                  >
                    Day {template.day} · {template.templateName} · {(template.languageCode || 'en').toUpperCase()}
                  </span>
                ))
              ) : inactivityTemplatesState.isInvalid ? (
                <span style={styles.formError}>
                  This bot’s inactivity template config is invalid. Review the stored JSON for malformed entries or duplicate days.
                </span>
              ) : (
                <span style={styles.inactivityActionHint}>
                  No inactivity templates are configured on this user’s bot.
                </span>
              )}
            </div>
          </div>
          <p style={styles.quickActionDescription}>
            {isPaused
              ? 'The bot is currently paused for this user. Click "Resume Bot" to reactivate automated messages.'
              : 'Use these actions to manage the user. "Send Inactivity Check-in" triggers the bot’s exact-match WhatsApp inactivity template and can optionally override the day. "Reset History" deletes all messages and resets the user to a NEW state. "Completely Delete User" removes the user record and associated data for end-to-end retesting. "Login as User" opens the user frontend with this user\'s session for debugging. "Generate Insights" runs AI behavioral analysis on recent messages.'}
          </p>
        </div>
        {togglePauseMutation.error && (
          <div style={styles.formError}>
            Error: {togglePauseMutation.error.message}
          </div>
        )}
        {resetHistoryMutation.error && (
          <div style={styles.formError}>
            Error: {resetHistoryMutation.error.message}
          </div>
        )}
        {deleteUserMutation.error && (
          <div style={styles.formError}>
            Error: {deleteUserMutation.error.message}
          </div>
        )}
        {impersonateMutation.error && (
          <div style={styles.formError}>
            Error: {impersonateMutation.error.message}
          </div>
        )}
        {generateInsightsMutation.error && (
          <div style={styles.formError}>
            Error: {generateInsightsMutation.error.message}
          </div>
        )}
        {sendManualInactivityMessageMutation.error && (
          <div style={styles.formError}>
            Error: {sendManualInactivityMessageMutation.error.message}
          </div>
        )}
      </div>

      {/* Behavioral Insights Section */}
      {insightsQuery.data && (
        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>Behavioral Insights</h3>
          <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 12px 0' }}>
            Generated {new Date(insightsQuery.data.generatedAt).toLocaleString()} from {insightsQuery.data.messageCount} messages over {insightsQuery.data.periodDays} days
          </p>

          {/* Dimensions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
            {insightsQuery.data.dimensions.map((dim) => {
              const trendIcon = dim.trend === 'improving' ? '\u2191' : dim.trend === 'declining' ? '\u2193' : '\u2192';
              const barColor = dim.score >= 70 ? '#22c55e' : dim.score >= 40 ? '#f59e0b' : '#ef4444';
              return (
                <div key={dim.name} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 500, minWidth: '160px', color: '#1e293b' }}>
                    {dim.name}
                  </span>
                  <div style={{ flex: 1, height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ width: `${dim.score}%`, height: '100%', backgroundColor: barColor, borderRadius: '4px', transition: 'width 0.3s ease' }} />
                  </div>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569', minWidth: '50px', textAlign: 'right' }}>
                    {dim.score} {trendIcon}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Patterns */}
          {insightsQuery.data.patterns.length > 0 && (
            <div>
              <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#334155', margin: '0 0 8px 0' }}>Patterns</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {insightsQuery.data.patterns.map((pattern, idx) => {
                  const tag = pattern.category === 'strength' ? { label: 'Strength', color: '#22c55e', bg: '#f0fdf4' }
                    : pattern.category === 'challenge' ? { label: 'Challenge', color: '#f59e0b', bg: '#fffbeb' }
                    : { label: 'Pattern', color: '#3b82f6', bg: '#eff6ff' };
                  return (
                    <div key={idx} style={{ padding: '8px 12px', backgroundColor: tag.bg, borderRadius: '6px', borderLeft: `3px solid ${tag.color}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 600, color: tag.color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {tag.label}
                        </span>
                        <span style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>
                          {pattern.title}
                        </span>
                      </div>
                      <p style={{ fontSize: '13px', color: '#475569', margin: 0 }}>{pattern.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Behavior Matrix Section */}
      <div style={styles.card}>
        <BehaviorMatrix
          activeQuadrant={
            (user.engagementScore ?? 0) > 50
              ? (user.preferredMessageTime && user.preferredMessageTime.includes('morning'))
                ? 'high-fast'
                : 'high-slow'
              : (user.preferredMessageTime && user.preferredMessageTime.includes('morning'))
                ? 'low-fast'
                : 'low-slow'
          }
        />
      </div>

      {/* Communication Schedule Section */}
      <div style={styles.card}>
        <SchedulePresetEditor
          currentPreset={user.profile ? extractSchedulePreset(user.profile.customData) : null}
          onUpdate={(preset) => {
            updateScheduleMutation.mutate({
              userId,
              schedule: preset,
            });
          }}
          isUpdating={updateScheduleMutation.isPending}
          error={updateScheduleMutation.error?.message || null}
        />
        <PauseDailyUpdatesToggle
          isPaused={user.profile ? extractPausedDailyUpdates(user.profile.customData) : false}
          onToggle={(paused) => {
            updatePauseDailyUpdatesMutation.mutate({
              userId,
              pausedDailyUpdates: paused,
            });
          }}
          isUpdating={updatePauseDailyUpdatesMutation.isPending}
          error={updatePauseDailyUpdatesMutation.error?.message || null}
        />
      </div>

      {/* User Settings Section - Name, Message Time, and Subscription Status */}
      <div style={styles.card}>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}>User Settings</h3>
          {!isEditing && (
            <button onClick={handleEditClick} style={styles.editButton}>
              Update
            </button>
          )}
        </div>

        {isEditing ? (
          <div style={styles.editForm}>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  style={styles.formInput}
                  placeholder="User name"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Message Time</label>
                <input
                  type="time"
                  value={editForm.preferredMessageTime}
                  onChange={(e) => setEditForm({ ...editForm, preferredMessageTime: e.target.value })}
                  style={styles.formInput}
                />
              </div>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Subscription Status</label>
                <select
                  value={editForm.subscriptionStatus}
                  onChange={(e) => setEditForm({ ...editForm, subscriptionStatus: e.target.value })}
                  style={styles.formSelect}
                >
                  <option value="">-- Select Status --</option>
                  <option value="trial">Trial</option>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Subscription Expires At</label>
                <input
                  type="datetime-local"
                  value={editForm.subscriptionExpiresAt ? editForm.subscriptionExpiresAt.slice(0, 16) : ''}
                  onChange={(e) => setEditForm({ ...editForm, subscriptionExpiresAt: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                  style={styles.formInput}
                />
              </div>
            </div>
            <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
              <label style={styles.formLabel}>Custom Data</label>
              <textarea
                value={editForm.customData}
                onChange={(e) => {
                  setEditForm({ ...editForm, customData: e.target.value });
                  if (customDataError) {
                    setCustomDataError(null);
                  }
                }}
                style={{
                  ...styles.formTextarea,
                  ...(customDataError ? styles.formTextareaError : {}),
                }}
                placeholder={'{\n  "key": "value"\n}'}
                spellCheck={false}
                    aria-invalid={customDataError ? 'true' : 'false'}
              />
              {hasLegacyCustomDataValue && !customDataError && (
                <div style={styles.formHint}>
                  Existing Custom Data is a legacy non-object value. This editor only saves JSON objects, so you must clear it or convert it to an object before saving.
                </div>
              )}
              {customDataError && (
                <div style={styles.formError}>
                  {customDataError}
                </div>
              )}
            </div>
            <div style={styles.formActions}>
              <button onClick={handleCancel} style={styles.cancelButton}>
                Cancel
              </button>
              <button
                onClick={handleSave}
                style={styles.saveButton}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
            {updateMutation.error && (
              <div style={styles.formError}>
                Error: {updateMutation.error.message}
              </div>
            )}
          </div>
        ) : (
          <div style={styles.detailsGrid}>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Name</span>
              <span style={styles.detailValue}>{user.name || '-'}</span>
            </div>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Message Time</span>
              <span style={styles.detailValue}>{user.preferredMessageTime || '-'}</span>
            </div>
          </div>
        )}
      </div>

      {/* Active Plan Section */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Active Plan</h3>
        <div style={styles.planSection}>
          <div style={styles.planInfo}>
            <span style={styles.planLabel}>Current Plan</span>
            <span style={styles.currentPlanName}>
              {plansQuery.data?.plans.find((plan) => plan.id === user.subscriptionPlanId)?.name || 'No Plan'}
            </span>
          </div>
          <div style={styles.planSelectContainer}>
            <label style={styles.planSelectLabel}>Change Plan</label>
            <select
              value={user.subscriptionPlanId ?? ''}
              onChange={(e) => handleChangePlan(e.target.value || null)}
              disabled={updatePlanMutation.isPending || plansQuery.isLoading}
              style={{
                ...styles.planSelect,
                opacity: updatePlanMutation.isPending ? 0.6 : 1,
              }}
            >
              <option value="">No Plan</option>
              {plansQuery.data?.plans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </select>
            {updatePlanMutation.isPending && (
              <span style={styles.planUpdating}>Updating...</span>
            )}
          </div>
          {updatePlanMutation.error && (
            <div style={styles.planError}>
              Error: {updatePlanMutation.error.message}
            </div>
          )}
        </div>
      </div>

      {/* Subscription Section */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Subscription</h3>
        <div style={styles.detailsGrid}>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Status</span>
            <span style={styles.detailValue}>
              <span style={{ ...styles.inlineBadge, ...getStatusStyle(user.subscriptionStatus ?? '') }}>
                {user.subscriptionStatus ?? 'Unknown'}
              </span>
            </span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Expires At</span>
            <span style={styles.detailValue}>
              {user.subscriptionExpiresAt
                ? new Date(user.subscriptionExpiresAt).toLocaleString()
                : 'N/A'}
            </span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Stripe Customer ID</span>
            <span style={styles.detailValue}>{user.stripeCustomerId || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Stripe Email</span>
            <span style={styles.detailValue}>{user.stripeCustomerEmail || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Access Code</span>
            <span style={styles.detailValue}>{user.stripeCheckoutAccessCode || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Checkout Session ID</span>
            <span style={styles.detailValue}>{user.stripeCheckoutSessionId || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Conversation State</span>
            <span style={styles.detailValue}>
              <span style={{ ...styles.inlineBadge, ...getConversationStateStyle(user.conversationState ?? '') }}>
                {user.conversationState ?? 'Unknown'}
              </span>
            </span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Churn Risk</span>
            <span style={styles.detailValue}>
              <span style={{ ...styles.inlineBadge, ...getChurnRiskStyle(user.churnRisk ?? '') }}>
                {user.churnRisk ?? 'Unknown'}
              </span>
            </span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Engagement Score</span>
            <span style={styles.detailValue}>{user.engagementScore ?? 0}/100</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Onboarding Step</span>
            <span style={styles.detailValue}>{user.onboardingStep ?? 0}</span>
          </div>
        </div>
      </div>

      {/* Acquisition Section */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Acquisition</h3>
        <div style={styles.detailsGrid}>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Registration Path</span>
            <span style={styles.detailValue}>{user.acquisition?.registrationPath || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Provider</span>
            <span style={styles.detailValue}>{user.acquisition?.provider || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Provider Participant ID</span>
            <span style={styles.detailValue}>{user.acquisition?.providerParticipantId || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Quiz Outcome</span>
            <span style={styles.detailValue}>{user.acquisition?.quizOutcome || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>UTM Source</span>
            <span style={styles.detailValue}>{user.acquisition?.utmSource || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>UTM Medium</span>
            <span style={styles.detailValue}>{user.acquisition?.utmMedium || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>UTM Campaign</span>
            <span style={styles.detailValue}>{user.acquisition?.utmCampaign || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>UTM Content</span>
            <span style={styles.detailValue}>{user.acquisition?.utmContent || '-'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>UTM Term</span>
            <span style={styles.detailValue}>{user.acquisition?.utmTerm || '-'}</span>
          </div>
        </div>
      </div>

      {/* Profile Section */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Profile</h3>
        {user.profile ? (
          <div style={styles.detailsGrid}>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Current Streak</span>
              <span style={styles.detailValue}>{extractCurrentStreakCount(user.profile.customData) ?? '-'}</span>
            </div>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Streak Last Activity</span>
              <span style={styles.detailValue}>
                {extractStreakLastActivityAt(user.profile.customData)
                  ? new Date(extractStreakLastActivityAt(user.profile.customData) as string).toLocaleString()
                  : '-'}
              </span>
            </div>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Last Period Start</span>
              <span style={styles.detailValue}>
                {user.profile.lastPeriodStart
                  ? new Date(user.profile.lastPeriodStart).toLocaleDateString()
                  : '-'}
              </span>
            </div>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Average Cycle Length</span>
              <span style={styles.detailValue}>
                {user.profile.averageCycleLength
                  ? `${user.profile.averageCycleLength} days`
                  : '-'}
              </span>
            </div>
            <div style={{ ...styles.detailItem, gridColumn: '1 / -1' }}>
              <span style={styles.detailLabel}>Goals</span>
              <span style={styles.detailValue}>
                {user.profile.goals && Array.isArray(user.profile.goals) && user.profile.goals.length > 0
                  ? (user.profile.goals as string[]).map((goal, idx) => (
                      <span key={idx} style={styles.tag}>{goal}</span>
                    ))
                  : '-'}
              </span>
            </div>
            <div style={{ ...styles.detailItem, gridColumn: '1 / -1' }}>
              <span style={styles.detailLabel}>Challenges</span>
              <span style={styles.detailValue}>
                {user.profile.challenges && Array.isArray(user.profile.challenges) && user.profile.challenges.length > 0
                  ? (user.profile.challenges as string[]).map((challenge, idx) => (
                      <span key={idx} style={styles.tag}>{challenge}</span>
                    ))
                  : '-'}
              </span>
            </div>
            {profileCustomDataDisplay ? (
              <div style={{ ...styles.detailItem, gridColumn: '1 / -1' }}>
                <div style={styles.detailLabelRow}>
                  <span style={styles.detailLabel}>Custom Data</span>
                  <button
                    type="button"
                    disabled={!canCopyCustomData}
                    onClick={handleCopyCustomData}
                    style={{
                      ...styles.copyJsonButton,
                      opacity: canCopyCustomData ? 1 : 0.6,
                      cursor: canCopyCustomData ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {copyCustomDataStatus === 'copied'
                      ? 'Copied'
                      : profileCustomDataDisplay.isJson
                      ? 'Copy JSON'
                      : 'Copy Value'}
                  </button>
                </div>
                <pre style={styles.jsonValue}>
                  {profileCustomDataDisplay.text}
                </pre>
                <div
                  aria-live="polite"
                  role="status"
                  style={styles.srOnly}
                >
                  {copyCustomDataAnnouncement}
                </div>
                {copyCustomDataStatus === 'error' && (
                  <div style={styles.formError}>
                    Unable to copy Custom Data.
                  </div>
                )}
              </div>
            ) : null}
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Profile Updated</span>
              <span style={styles.detailValue}>
                {user.profile.updatedAt
                  ? new Date(user.profile.updatedAt).toLocaleString()
                  : '-'}
              </span>
            </div>
          </div>
        ) : (
          <p style={styles.emptyText}>No profile data available</p>
        )}
      </div>

      {/* Phase Progress Section - only shown for phased_journey bots */}
      {user.bot?.communicationSchedule && (
        <PhaseProgressSection
          userId={userId}
          userName={user.name}
          customData={user.profile?.customData}
          communicationSchedule={user.bot.communicationSchedule}
        />
      )}

      {/* Bot Section */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Assigned Bot</h3>
        {user.bot ? (
          <div style={styles.botInfo}>
            <div style={styles.botHeader}>
              <span style={styles.botIcon}>{user.bot.icon || '🤖'}</span>
              <div style={styles.botDetails}>
                <h4 style={styles.botName}>{user.bot.name}</h4>
                <p style={styles.botSlug}>/{user.bot.slug}</p>
              </div>
              <span
                style={{
                  ...styles.inlineBadge,
                  ...getBotStatusStyle(user.bot.status ?? ''),
                }}
              >
                {user.bot.status ?? 'Unknown'}
              </span>
            </div>
            {user.bot.description && (
              <p style={styles.botDescription}>{user.bot.description}</p>
            )}
            <button
              onClick={() => navigate(`/bots/${user.bot!.id}`)}
              style={styles.viewBotButton}
            >
              View Bot Details
            </button>
          </div>
        ) : (
          <p style={styles.emptyText}>No bot assigned</p>
        )}
      </div>

      {/* Activity Summary */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Activity Summary</h3>
        <div style={styles.detailsGrid}>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Total Messages</span>
            <span style={styles.detailValue}>{user.activity.totalMessages}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Last Daily Message</span>
            <span style={styles.detailValue}>
              {user.lastDailyMessageAt
                ? new Date(user.lastDailyMessageAt).toLocaleString()
                : 'Never'}
            </span>
          </div>
          {user.activity.lastMessage && (
            <div style={{ ...styles.detailItem, gridColumn: '1 / -1' }}>
              <span style={styles.detailLabel}>Last Message</span>
              <div style={styles.lastMessageBox}>
                <span style={styles.messageDirection}>
                  {user.activity.lastMessage.direction === 'inbound' ? '← Received' : '→ Sent'}
                </span>
                <p style={styles.messageContent}>
                  {user.activity.lastMessage.content.length > 200
                    ? `${user.activity.lastMessage.content.substring(0, 200)}...`
                    : user.activity.lastMessage.content}
                </p>
                <span style={styles.messageTime}>
                  {user.activity.lastMessage.createdAt
                    ? new Date(user.activity.lastMessage.createdAt).toLocaleString()
                    : 'Unknown'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scheduled Messages */}
      <div style={styles.card}>
        <ScheduledMessagesCard userId={userId} />
      </div>

      {/* Message Center */}
      <div style={styles.card}>
        <MessageCenter userId={userId} />
      </div>
    </div>
  );
}

function getStatusStyle(status: string): React.CSSProperties {
  switch (status) {
    case 'active':
      return { backgroundColor: '#dcfce7', color: '#166534' };
    case 'trial':
      return { backgroundColor: '#fef9c3', color: '#854d0e' };
    case 'expired':
    case 'cancelled':
      return { backgroundColor: '#fee2e2', color: '#991b1b' };
    default:
      return { backgroundColor: '#f1f5f9', color: '#475569' };
  }
}

/**
 * Updated status style function matching the prototype design.
 */
function getStatusStyleUpdated(status: string): React.CSSProperties {
  switch (status) {
    case 'active':
      return { backgroundColor: 'rgba(152, 184, 154, 0.15)', color: '#5A8A5E' };
    case 'trial':
      return { backgroundColor: 'rgba(184, 196, 232, 0.2)', color: '#5A6A8A' };
    case 'pending_activation':
    case 'expired':
      return { backgroundColor: 'rgba(232, 200, 140, 0.2)', color: '#9B7B3C' };
    case 'churned':
    case 'cancelled':
      return { backgroundColor: 'rgba(201, 140, 130, 0.15)', color: '#8B5A50' };
    default:
      return { backgroundColor: 'rgba(93, 86, 78, 0.1)', color: '#5C564E' };
  }
}

function getConversationStateStyle(state: string): React.CSSProperties {
  switch (state) {
    case 'active':
      return { backgroundColor: '#dcfce7', color: '#166534' };
    case 'onboarding':
      return { backgroundColor: '#dbeafe', color: '#1e40af' };
    case 'new':
      return { backgroundColor: '#fef9c3', color: '#854d0e' };
    case 'paused':
      return { backgroundColor: '#f1f5f9', color: '#475569' };
    default:
      return { backgroundColor: '#f1f5f9', color: '#475569' };
  }
}

function getChurnRiskStyle(risk: string): React.CSSProperties {
  switch (risk) {
    case 'low':
      return { backgroundColor: '#dcfce7', color: '#166534' };
    case 'medium':
      return { backgroundColor: '#fef9c3', color: '#854d0e' };
    case 'high':
      return { backgroundColor: '#fee2e2', color: '#991b1b' };
    default:
      return { backgroundColor: '#f1f5f9', color: '#475569' };
  }
}

function getBotStatusStyle(status: string): React.CSSProperties {
  switch (status) {
    case 'active':
      return { backgroundColor: '#dcfce7', color: '#166534' };
    case 'inactive':
      return { backgroundColor: '#fee2e2', color: '#991b1b' };
    case 'archived':
      return { backgroundColor: '#f1f5f9', color: '#475569' };
    default:
      return { backgroundColor: '#f1f5f9', color: '#475569' };
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '900px',
  },
  header: {
    marginBottom: '24px',
  },
  backButton: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#3b82f6',
    fontSize: '14px',
    cursor: 'pointer',
    marginBottom: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#1e293b',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#64748b',
  },
  error: {
    padding: '16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    marginBottom: '20px',
  },
  userHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: '1px solid #f1f5f9',
  },
  avatar: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: '#e2e8f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    fontWeight: '600',
    color: '#5C564E',
  },
  userInfo: {
    flex: 1,
  },
  userName: {
    fontFamily: '"Sentient", Georgia, serif',
    fontSize: '22px',
    fontWeight: '600',
    color: '#3D3A36',
    marginBottom: '4px',
    margin: 0,
  },
  userId: {
    fontSize: '14px',
    color: '#94a3b8',
  },
  userEmail: {
    fontSize: '13px',
    color: '#9B9285',
    margin: 0,
  },
  statusCardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    marginBottom: '24px',
  },
  statusBadgeSmall: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  engagementValue: {
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  avgResponseValue: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#3D3A36',
    margin: 0,
  },
  subscriptionCard: {
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '20px',
  },
  subscriptionTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#5C564E',
    marginBottom: '12px',
    margin: 0,
    marginBlockEnd: '12px',
  },
  subscriptionContent: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  subscriptionPlan: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#3D3A36',
    textTransform: 'capitalize',
    margin: 0,
  },
  subscriptionSince: {
    fontSize: '12px',
    color: '#9B9285',
    margin: 0,
    marginTop: '4px',
  },
  subscriptionRight: {
    textAlign: 'right',
  },
  subscriptionExpiry: {
    fontSize: '12px',
    color: '#9B7B3C',
    margin: 0,
  },
  subscriptionExpiryDate: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#3D3A36',
    margin: 0,
    marginTop: '4px',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '6px 14px',
    borderRadius: '9999px',
    fontSize: '14px',
    fontWeight: '500',
  },
  inlineBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: '500',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '16px',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  detailLabel: {
    fontSize: '12px',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  detailLabelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  detailValue: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#334155',
  },
  editButton: {
    padding: '8px 16px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  editForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  formLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#334155',
  },
  formSelect: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    color: '#334155',
    backgroundColor: '#ffffff',
  },
  formInput: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    color: '#334155',
  },
  formTextarea: {
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '220px',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '13px',
    color: '#334155',
    fontFamily: 'monospace',
    resize: 'vertical',
  },
  formTextareaError: {
    border: '1px solid #dc2626',
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '8px',
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  saveButton: {
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  formError: {
    padding: '12px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    fontSize: '14px',
  },
  formHint: {
    padding: '12px',
    backgroundColor: '#fff7ed',
    color: '#9a3412',
    borderRadius: '8px',
    fontSize: '14px',
  },
  emptyText: {
    color: '#94a3b8',
    fontSize: '14px',
  },
  tag: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    borderRadius: '4px',
    fontSize: '13px',
    marginRight: '8px',
    marginBottom: '4px',
  },
  jsonValue: {
    backgroundColor: '#f8fafc',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '13px',
    fontFamily: 'monospace',
    overflow: 'auto',
    maxHeight: '200px',
    color: '#334155',
    margin: 0,
  },
  copyJsonButton: {
    padding: '6px 10px',
    backgroundColor: '#ffffff',
    color: '#3b82f6',
    border: '1px solid #bfdbfe',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  srOnly: srOnlyStyles,
  botInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  botHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  botIcon: {
    fontSize: '32px',
  },
  botDetails: {
    flex: 1,
  },
  botName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0,
  },
  botSlug: {
    fontSize: '14px',
    color: '#94a3b8',
    fontFamily: 'monospace',
    margin: 0,
  },
  botDescription: {
    fontSize: '14px',
    color: '#64748b',
    lineHeight: '1.5',
    margin: 0,
  },
  viewBotButton: {
    alignSelf: 'flex-start',
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: '#3b82f6',
    border: '1px solid #3b82f6',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    marginTop: '8px',
  },
  lastMessageBox: {
    backgroundColor: '#f8fafc',
    padding: '12px',
    borderRadius: '8px',
    marginTop: '8px',
  },
  messageDirection: {
    fontSize: '12px',
    color: '#64748b',
    fontWeight: '500',
  },
  messageContent: {
    fontSize: '14px',
    color: '#334155',
    lineHeight: '1.5',
    margin: '8px 0',
  },
  messageTime: {
    fontSize: '12px',
    color: '#94a3b8',
  },
  quickActionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  quickActionsButtonRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
  },
  quickActionButton: {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    alignSelf: 'flex-start',
  },
  pauseButton: {
    backgroundColor: '#f59e0b',
    color: '#ffffff',
  },
  resumeButton: {
    backgroundColor: '#22c55e',
    color: '#ffffff',
  },
  sendCheckInButton: {
    backgroundColor: '#3b82f6',
    color: '#ffffff',
  },
  resetHistoryButton: {
    backgroundColor: '#ef4444',
    color: '#ffffff',
  },
  deleteUserButton: {
    backgroundColor: '#991b1b',
    color: '#ffffff',
  },
  loginAsUserButton: {
    backgroundColor: '#8b5cf6',
    color: '#ffffff',
  },
  generateInsightsButton: {
    backgroundColor: '#10b981',
    color: '#ffffff',
  },
  quickActionButtonDisabled: {
    backgroundColor: '#94a3b8',
    cursor: 'not-allowed',
  },
  inactivityActionPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '14px',
    borderRadius: '10px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
  },
  inactivityActionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  inactivityActionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e293b',
  },
  inactivityActionMeta: {
    fontSize: '13px',
    color: '#475569',
  },
  inactivityActionControls: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  inactivityActionSelect: {
    width: '100%',
    maxWidth: '360px',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    backgroundColor: '#ffffff',
    color: '#1e293b',
    fontSize: '14px',
  },
  inactivityActionHint: {
    fontSize: '13px',
    color: '#64748b',
    lineHeight: '1.5',
  },
  inactivityTemplateList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  inactivityTemplateTag: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 10px',
    borderRadius: '999px',
    backgroundColor: '#e0f2fe',
    color: '#0f172a',
    fontSize: '12px',
    fontWeight: '500',
  },
  quickActionDescription: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0,
    lineHeight: '1.5',
  },
  planSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  planInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  planLabel: {
    fontSize: '12px',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  currentPlanName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#3D3A36',
  },
  planSelectContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxWidth: '300px',
  },
  planSelectLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#334155',
  },
  planSelect: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    color: '#334155',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
  },
  planUpdating: {
    fontSize: '13px',
    color: '#3b82f6',
    fontWeight: '500',
  },
  planError: {
    padding: '12px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    fontSize: '14px',
  },
};

export default UserDetailPage;
